export const en = {
  app: {
    title: "Arch-designer",
    tagline:
      "Model together in one repository: ArchiMate 4, C4, Sharp and more. Every diagram is a view on the same model.",
    status: {
      title: "API connection",
      loading: "Loading…",
      error: "Error",
      superAdminNoWorkspace:
        "You're a super admin but not (yet) a member of a workspace. Open ⚙ Settings → Workspace → Switch workspace to pick one.",
      user: "Signed in as",
      language: "UI language",
      workspaces: "Workspaces",
    },
    header: {
      settings: "⚙ Settings",
      settingsTitle: "Settings (F10)",
      settingsMenu: "⚙ Settings",
      settingsMenuTitle: "Open settings menu (F10)",
      sourceCode: "Source code (AGPL-3.0)",
      readOnly: "Read only",
      readOnlyTitle:
        "You have read access to this workpackage: you can look, but not change anything. Ask a workspace admin for write access.",
      settingsMenuGeneralGroup: "General",
      settingsMenuInstallGroup: "Installation",
      settingsMenuAdminGroup: "Admin",
      settingsMenuPending: "Not yet wired in dropdown — use the legacy Settings button",
      logoutAs: "Log out ({{email}})",
      logoutTitle: "Logout",
      displayModeV31: "3.x",
      displayModeV31Title:
        "ArchiMate 3.x view — shows the familiar layer-specific types (Business Process, Application Service, …). Presentation only; the model does not change.",
      displayModeV4: "4",
      displayModeV4Title:
        "ArchiMate 4 view — shows the generic types (Process, Service, …) with the 3.x origin as a «stereotype» badge. Presentation only; the model does not change.",
      chooseWorkspace: "Choose workspace",
      scopeIndicatorTitle: "Active focus scope (F7 to switch)",
      crdt: {
        connecting: "connecting…",
        connectingTitle: "Connecting to the realtime server",
        syncing: "syncing…",
        syncingTitle: "Initial sync with the other editors in progress",
        connected: "live",
        connectedTitle: "Connected — changes by others appear automatically",
        offline: "offline",
        offlineTitle: "No connection to the realtime server — retrying…",
      },
    },
    panels: {
      explorer: "Explorer",
      concepts: "Concepts",
      types: "Types",
      diagrams: "Diagrams",
      relations: "Relations",
      openPanelTitle: "Open {{label}} panel",
      resizeHandleTitle: "Drag to resize — double-click to reset to default",
    },
    kladblok: {
      hint: "Scratchpad — drag concepts here from the Foldertree",
      clear: "Clear",
      clearTitle: "Clear scratchpad (concepts + relations stay in DB)",
      conceptNotFound: "Concept '{{conceptId}}' not found in the workspace store.",
      endpointsNotFound: "Source or target concept no longer found in the workspace store.",
      createRelationFailed: "Creating relation failed: {{msg}}",
      dismiss: "close",
      dismissAria: "Close",
      picker: {
        title: "Choose relation type",
        noneBefore: "No valid relation types between",
        noneAnd: "and",
        noneAfter: "according to the matrix.",
        reversed: "{{label}} (reversed)",
      },
    },
    editor: {
      fixNestedDone: "Fix: {{n}} concept(s) linked to their container.",
      fixNestedNone: "Fix: no nesting corrections needed on this diagram.",
    },
  },
  ribbon: {
    groups: {
      font: "Font/Text Alignment",
      colour: "Colour",
      align: "Align objects",
      textInObject: "Text in object",
      size: "Size",
      line: "Line",
      zStack: "z-order",
      overlay: "Overlay",
    },
    groupsShort: {
      font: "Text",
      colour: "Colour",
      align: "Align",
      zStack: "Order",
      textInObject: "Text position",
      size: "Size",
      line: "Line",
      overlay: "Overlay",
    },
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    fontFamily: "Font family",
    fontSize: "Font size",
    textColour: "Text colour",
    fillColour: "Fill colour",
    lineColour: "Line colour",
    labelVertical: "Turn the text a quarter turn",
    clearFormatting: "Clear formatting",
    alignLeft: "Align left",
    alignCenter: "Center",
    alignCenterX: "Center horizontally",
    alignRight: "Align right",
    labelWrapHyphenate: "Hyphenate long words (syllable-aware)",
    labelWrapBreakAnywhere: "Break long words mid-word",
    alignTop: "Align top",
    alignCenterY: "Center vertically",
    alignBottom: "Align bottom",
    label: {
      topLeft: "Top left",
      top: "Top",
      topRight: "Top right",
      left: "Left",
      center: "Center",
      right: "Right",
      bottomLeft: "Bottom left",
      bottom: "Bottom",
      bottomRight: "Bottom right",
      outside: "outside",
      outsideSuffix: " (outside)",
      outsideTitle: "Outside — label sits outside the box in the chosen direction",
      hideIconTitle: "Hide the type icon at the top right of the selected objects",
      hideIcon: "Hide icon",
    },
    equalWidth: "Equal width",
    equalHeight: "Equal height",
    equalBoth: "Equal width and height",
    shapeEllipse: "Ellipse / circle (border-radius 50%) — toggle",
    shapeCircle: "Make circle (square + border-radius 50%)",
    shapeSquare: "Make square",
    zStack: {
      forward: "Bring forward",
      backward: "Send backward",
      toFront: "Bring to front",
      toBack: "Send to back",
    },
    orthogonalize:
      "Straighten line — every segment becomes purely horizontal or vertical based on its current direction",
    swatch: {
      defaultLabel: "Default (from theme)",
      transparent: "Transparent",
      themeLight: "ArchiMate layer (light)",
      themeDark: "ArchiMate layer (dark)",
      standardColours: "Standard colours",
      recent: "Recent",
      moreColours: "More colours…",
      eyedropperTitle: "Pick a colour anywhere on the screen",
      eyedropper: "Pick colour from screen",
    },
    default: "Default",
    straightenNoHorizontalOverlap:
      "Cannot straighten the line — source and target do not overlap horizontally.",
    straightenNoVerticalOverlap:
      "Cannot straighten the line — source and target do not overlap vertically.",
    strokeColourPicker: "Line colour (border of the object)",
    strokeColourTitle: "Line colour — border of selected object(s)",
    lineColourMulti: "Line colour — apply to {{n}} lines",
    labelBg: {
      title: "Label background",
      multi: "Label background — apply to {{n}} lines",
      set: "Label background — click to clear or pick another colour",
      unset: "Label background — transparent by default; pick a colour for an opaque box",
    },
    labelText: {
      title: "Label text colour",
      multi: "Label text colour — apply to {{n}} lines",
      set: "Label text colour — click to clear or pick another colour",
      unset: "Label text colour — slate by default; pick a colour for accent",
    },
    routing: {
      multi: "Routing — apply to {{n}} lines",
      single: "Routing of the selected line",
      manual: "Manual",
    },
    lineStyle: {
      multi: "Line style — apply to {{n}} lines",
      single: "Line style of the selected line",
      borderMulti: "Border style — apply to {{n}} concepts",
      borderSingle: "Border style of the selected concept",
      solid: "— Solid",
      dashed: "- - Dashed",
      dotted: "··· Dotted",
      none: "None (invisible)",
    },
    helpTitle: "Open the user guide (Cmd/Ctrl+/)",
    formatPainter: {
      noFormat:
        "First select an object with formatting (fill, border colour, line style) before using the magic wand.",
      armed:
        "Magic wand active — select target objects and click again to apply the formatting. Click without a selection to cancel.",
      idle: "Magic wand — first select a source object with the desired formatting and click here; then select target objects and click again.",
    },
  },
  overlay: {
    title: "Colour overlay",
    toolbarTitle: "Colour overlay (rules on attribute/type)",
    close: "Close",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    noDiagram: "No diagram open.",
    customRenderNote:
      "This diagram type has its own render — the overlay only applies to standard diagrams.",
    enable: "Overlay on",
    strategy: "On multiple:",
    firstMatch: "first rule wins",
    lastMatch: "last rule wins",
    noRules: "No rules yet. Add one to colour or dim.",
    ruleEnabled: "Rule active",
    moveUp: "Move rule up (higher precedence)",
    moveDown: "Move rule down",
    unnamedRule: "(unnamed rule)",
    addRule: "Add rule",
    clear: "Clear overlay",
    ruleCount: "{{count}} rule(s)",
    condition: "Condition",
    anyType: "Any type",
    anyStereotype: "Any stereotype",
    anyLayer: "Any layer",
    anyMatch: "any element",
    labelPlaceholder: "Name (e.g. Phased-out apps)",
    attrNamePlaceholder: "Attribute name (optional)",
    attrNone: "— no attribute —",
    pickValue: "Pick value…",
    hasAttrType: "Has attribute type",
    childNone: "— no attribute type —",
    relatedTo: "Has a relation with (name)",
    relatedToPlaceholder: "e.g. Party",
    relatedToShort: "related to",
    aiPlaceholder:
      'Describe in words, e.g. "colour phased-out applications red and dim the technology layer"',
    aiGenerate: "Generate with AI",
    aiBusy: "Working…",
    aiAdded: "{{count}} rule(s) added",
    aiNoRules:
      "No usable rules from your description. Be more concrete using existing types/attributes.",
    aiFailed: "Generation failed. Please try again.",
    valuePlaceholder: "Value",
    valueListPlaceholder: "value1, value2, …",
    effect: "Effect",
    fill: "Fill colour",
    dim: "Dim",
    cmp: {
      eq: "equals",
      neq: "is not",
      contains: "contains",
      in: "is one of",
      gt: ">",
      gte: "≥",
      lt: "<",
      lte: "≤",
      exists: "is set",
      not_exists: "is empty",
    },
  },
  simplify: {
    title: "Simplify",
    toolbarTitle: "Simplify — generate a simplified copy",
    close: "Close",
    noDiagram: "No diagram open.",
    intro:
      "Generates a simplified copy; the original stays unchanged. Pick one or more transformations.",
    hideLeaves: "Hide leaf endpoints",
    hideLeavesHint: "Hide concepts with no outgoing relation (often read-only data objects).",
    cluster: "Roll up compositions",
    clusterHint: "Hide the parts of a Composition/Aggregation; the 'whole' side stays.",
    criticalPath: "Only the path between selection",
    criticalPathHint: "First select exactly 2 elements on the canvas.",
    criticalPathReady: "Shows only the shortest path between the 2 selected elements.",
    collapseLayer: "Collapse layer into one block",
    collapseLayerHint:
      "All elements of the chosen layer(s) are replaced by a single collapsed block; their relations attach to that block.",
    promote: "Promote to abstraction",
    promoteHint:
      "Collapse a service with its realizing components/functions into one ApplicationCollaboration block. Tick a suggested cluster or use your current selection.",
    promoteSelection: "Current selection ({{count}} elements)",
    autoLayout: "Auto-arrange (tidy alignment)",
    generate: "Generate simplified copy",
    busy: "Working…",
    done: "Copy created — {{count}} element(s) hidden.",
    failed: "Simplification failed. Please try again.",
  },
  supernode: {
    defaultLabel: "Collapsed group",
    badge: "{{count}} element(s)",
    badgeTooltip: "Number of collapsed elements in this block",
    layers: {
      strategy: "Strategy",
      business: "Business",
      application: "Application",
      technology: "Technology",
      physical: "Physical",
      motivation: "Motivation",
      implementation_migration: "Implementation & migration",
      composite: "Composite",
    },
  },
  groupEnclose: {
    title: "Turn this into a group?",
    description:
      '"{{group}}" now encloses these objects. Grouping nests them inside it; pick per object which relation belongs to that. Not grouping leaves it a plain backdrop.',
    allTo: "All to:",
    choose: "— pick —",
    noRelation: "no relation",
    nothingAllowed: "no relation allowed",
    cancel: "Do not group",
    confirm: "Group",
    fixedSharp: "aggregation (Sharp level)",
    fixedNone: "no relation needed",
  },
  canvas: {
    noDiagramOpen: "No diagram open — pick or create a diagram",
    viewStateConflict: {
      notSaved_one:
        "'{{names}}' has not been saved yet — a colleague changed this occurrence in the meantime. Your version is still there; move it again to save it after all.",
      notSaved_other:
        "{{count}} occurrences have not been saved yet ({{names}}) — a colleague got there first. Your version is still there; move them again to save them after all.",
      unnamed: "(unnamed)",
      relation: "(relation)",
      object: "an object",
    },
    edge: {
      ariaLabel: "Relation from {{source}} to {{target}}",
    },
    hiddenBadge: {
      tooltip_one: "{{count}} hidden sub-element",
      tooltip_other: "{{count}} hidden sub-elements",
    },
    hiddenTotal: {
      label_one: "{{count}} hidden on this diagram",
      label_other: "{{count}} hidden on this diagram",
      tooltip:
        "Number of elements on this diagram with `hidden` flag. Click a nested container to see its share.",
    },
    collapseButton: {
      collapse: "Collapse container — hide all nested elements",
      expand: "Expand container — show hidden elements again",
    },
    contextMenu: {
      edit: "Edit",
      selectAll: "Select all",
      fitView: "Fit to view",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      zoomReset: "Reset zoom",
      delete: "Delete",
      merge: "Merge",
      mergeConceptsNotClones:
        "The selected objects are not identical (same type, name and stereotypes) — merging is not possible.",
      mergeRelationsNotClones:
        "The selected relations are not identical (same type, name and direction) — merging is not possible.",
      changeArchimateType: "Change ArchiMate type",
      showInExplorer: "Show in Explorer",
    },
    nesting: {
      unnamed: "(unnamed)",
      onDiagrams: "on: {{names}}",
      visibleEverywhere: "visible everywhere",
      notPlaced: "not placed",
      title: {
        pickRelationType: "Which type of relation?",
        pickHiddenRelation: "Which relation do you want to show again?",
        mergeRelation: "Merge relation?",
        mergeConcept: "Merge object?",
        pickExistingOrNew: "Use an existing relation as template?",
      },
      description: {
        pickRelationType:
          "You placed a concept inside another. Choose which type of relation goes with it, or cancel for visual nesting only.",
        pickHiddenRelation:
          "There are several hidden relations between these two concepts on this diagram. Choose which one you want to show again.",
        mergeRelation:
          "This relation is identical to an existing one (same type, name and direction). Choose an existing one to merge with — note: the changed metadata of this relation is then discarded in favour of the chosen existing one. Or keep both.",
        mergeConcept:
          "This object is identical to an existing one (same type, name and stereotypes). Choose an existing one to merge with — note: the changed metadata of this object is then discarded in favour of the chosen existing one. Or keep both.",
        pickExistingOrNew:
          "Relations already exist between these concepts. Choose one as template (same type + properties → new relation instance on these endpoints), or create a fresh relation with the default relation type.",
      },
      noOptions: "No valid options available.",
      newRelation: "+ New relation (default type from matrix)",
      keepBoth: "Keep both (do not merge)",
      noRelation: "No relation",
      removeOld_one: "Also delete the relation to «{{parent}}» ({{relations}}) from the model",
      removeOld_other:
        "Also delete the {{count}} relations to «{{parent}}» ({{relations}}) from the model",
      removeOldEverywhere:
        "These relations show on every diagram where both objects are; deleting removes them there too.",
      removeOldElsewhere: "Also shown on: {{names}}. Deleting removes them there too.",
      removeOldOnlyHere: "Not shown on any other diagram.",
      removeOldConfirm_one:
        "Also delete the relation to «{{parent}}» from the model? It goes to the trash and is removed from every diagram.",
      removeOldConfirm_other:
        "Also delete the {{count}} relations to «{{parent}}» from the model? They go to the trash and are removed from every diagram.",
    },
    relationMenu: {
      relationType: "Relation type",
      reverseDirection: "Reverse direction",
      reversed: "{{label}} (reversed)",
      directedOn: "✓ Directed (half arrow)",
      directed: "Directed (half arrow)",
      appearance: "Appearance",
      routing: "Line routing",
      clearWaypoints: "Clear waypoints",
    },
    pathRealization: {
      fallbackPath: "the path",
      fallbackElement: "this element",
      confirm:
        "A path is realized by the element, not the other way round.\n\nShall I create a realization from '{{element}}' to '{{path}}'?",
      failed: "The realization could not be created.",
    },
    relationNotAllowedByViewKind:
      "The diagram type does not allow relations between '{{source}}' and '{{target}}'.",
    noValidRelation:
      "No valid relation between '{{source}}' and '{{target}}' according to the Relationship Matrix.",
    dropSkipped: "{{n}} concept(s) skipped — type not allowed on a diagram of type '{{viewKind}}'.",
    typeNotAllowed: "Type '{{type}}' is not allowed on a diagram of type '{{viewKind}}'.",
    relationNeedsBothEnds:
      "Relation cannot be drawn: '{{source}}' and '{{target}}' must both be on this diagram.",
    bmc: {
      dropSkipped_one: "{{count}} concept skipped — it does not fit in this slot.",
      dropSkipped_other: "{{count}} concepts skipped — they do not fit in this slot.",
      noTemplate: "No canvas template configured.",
      unassigned: "Unassigned — drag into a slot",
      dropRejected: "Slot '{{slot}}' only accepts: {{allowed}}",
      moveUp: "Move up in stack",
      moveDown: "Move down in stack",
    },
    unnamed: "(unnamed)",
    node: {
      nameTitle:
        "Double-click to change the name in this language; drag to move; Cmd+double-click resets the position",
    },
    sharp: {
      attribute: "Attribute",
      identifierTitle: "Identifier — click = select, right-click = edit/delete, drag = reorder",
      attributeTitle: "Attribute — click = select, right-click = edit/delete, drag = reorder",
    },
    listBox: {
      noTitle: "(no title)",
      empty: "Drag objects or diagrams here…",
      openDiagram: "Click to open diagram",
      removeHint: "Right-click to remove",
    },
    edgeLabel: {
      widthPillTitle: "Drag to adjust the width · double-click to reset",
      selectedTitle:
        "Drag to move. Double-click = rename. Grab the ⇔ handle on the right to adjust the width. Cmd/Alt+double-click = back to default.",
      unselectedTitle: "Click the line to select it. Double-click the label = rename.",
      widthHandleTitle: "Drag to adjust the label width. Double-click to reset.",
      widthHandleAria: "Adjust label width",
    },
  },
  uiGlossary: {
    button: "Terms on this screen",
    title: "Terms on this screen",
  },
  changeArchimateType: {
    title: "Change ArchiMate type",
    intro: "Choose the business-layer type for «{{name}}». The Concept stereotype is kept.",
    currentBadge: "current",
    noImpact: "No relations become invalid from this change.",
    impactWarning_one:
      "1 relation cannot be kept under the new type and will be converted to a plain Association:",
    impactWarning_other:
      "{{count}} relations cannot be kept under the new type and will be converted to plain Associations:",
    apply: "Change type",
    cancel: "Cancel",
    applying: "Working…",
  },
  branchPicker: {
    modal: {
      title: "Create new branch",
      name: "Name",
      namePlaceholder: 'e.g. "feature/api-redesign"',
      fromBranch: "Clone from branch",
      main: "main",
      fromSnapshot: "Or start from snapshot",
      snapshotNone: "— None (use branch HEAD)",
      noSnapshots: "No snapshots available for this branch.",
      cancel: "Cancel",
      create: "OK",
      creating: "Creating…",
      errorEmptyName: "Enter a branch name.",
    },
  },
  attributePanel: {
    cweLibrary: {
      openButton: "Choose from CWE library",
      closeButton: "Close",
      searchPlaceholder: "Search by CWE name...",
      noResults: "No results.",
      notConfigured: "MITRE CWE sync isn't configured for this installation.",
      cveHeading: "CVEs for {{cweId}} (NVD):",
      skipButton: "Skip",
      noCveResults: "No CVEs found at NVD for this CWE.",
    },
    headerConcept: "Concept",
    headerRelation: "Relation",
    headerDiagram: "Diagram",
    headerEmpty: "No selection",
    emptyHint: "Select a node or an edge on the canvas.",
    sectionType: "Type",
    subtypeLabel: "Subtype",
    subtypeNone: "{{type}} (no subtype)",
    sectionLabel: "Label",
    sectionAttributes: "Attributes",
    multiplicityDiscouraged:
      "Multiplicity is rarely meaningful on this relation type — ArchiMate 4 allows it, but the referent is undefined here. Consider whether your intent belongs in a «Concept Constraint» instead.",
    sectionStereotypes: "Stereotypes",
    sectionLineRouting: "Line routing",
    sectionColours: "Colours",
    sectionEndpoints: "Endpoints",
    sectionOwner: "Owner",
    nameNl: "Name (NL)",
    nameEn: "Name (EN)",
    nameDerivedHint:
      "Derived from the linked attribute type — change it via the Attribute type field.",
    descriptionDerivedHint:
      "Derived from the linked attribute type's definition — change it in the Glossary.",
    descriptionNl: "Description (NL)",
    descriptionEn: "Description (EN)",
    labelNl: "Label (NL)",
    labelEn: "Label (EN)",
    altLabels: {
      title: "Alternative labels (for non-ArchiMate diagrams)",
      hint: "Optional — shows a custom name in C4/Sharp/BMC diagrams. Leave empty to use the regular name.",
      diagramsWord: "diagrams",
    },
    colourFill: "Fill",
    colourStroke: "Stroke",
    colourText: "Text",
    colourLabelBg: "Label bg",
    routing: {
      smoothstep: "Smoothstep (right angles, rounded)",
      step: "Step (right angles)",
      straight: "Straight",
      bezier: "Bezier (smooth)",
      manual: "Manual (waypoints)",
    },
    endpointFrom: "from",
    endpointTo: "to",
    readonlyOwnerHint: "Read-only: only the owner or a workspace admin can edit name/type/parent.",
    versionLabel: "Version {{n}}",
    historyTitle: "Show version history",
    historyAria: "Version history",
    closeAria: "Collapse",
    instance: {
      loseLocalValuesConfirm:
        "Warning: the local values of this instance will be lost. The relation's values will be used from now on. Continue?",
      storeOnInstanceLabel: "Store data on the instance on the diagram",
      storeOnRelation: "No — on the relation",
      storeOnThisInstance: "Yes — on this instance",
      revertTitle: "Reset values + flag to when you opened this panel",
      revertButton: "Revert changes",
    },
    unknownViewKind: "{{viewKind}} (unknown)",
    configuration: "Configuration",
    yes: "Yes",
    no: "No",
    chooseOption: "— choose —",
    optgroupTypes: "Types",
    colourSlotTitle: "{{label}} — click to change",
    transparent: "Transparent",
    colourDefault: "Default",
    relationTypeCurrent: "{{label}} (current)",
    reverseDirection: "Reverse direction",
    relationTypeReversed: "{{label}} (reversed)",
    forwardCount: "{{n}} forward",
    reversedCountSuffix: ", {{n}} reversed",
    link: {
      title: "Link",
      none: "None",
      url: "URL",
    },
    sectionNotation: "Notation",
    notationRectangle: "Rectangle",
    notationArchimate: "ArchiMate symbol",
    bilingual: {
      translateFailed: "Translation failed: {{error}}",
      translating: "translating…",
    },
    fields: {
      ownerOnly: "owner only",
      commaSeparated: "comma-separated",
      commaSeparatedPerLanguage: "comma-separated per language",
      unsupportedType: "unsupported type: {{type}}",
      deletedRef: "(deleted) {{id}}…",
      none: "none",
      removeRef: "Remove {{label}}",
      addRef: "+ add…",
      addFacet: "+ add facet…",
      newFacet: "＋ New {{type}} “{{name}}”",
      sameNameSuffix: " (same name)",
      addButton: "Add",
      enumTbd: "— to be chosen (required) —",
    },
  },
  explorer: {
    collapseAll: "Collapse all groups",
    detail: {
      sectionProperties: "Properties",
      sectionRelations: "Relations",
      sectionDiagramOccurrences: "Diagram occurrences",
      sectionAttributes: "Attributes",
      sectionObjectReferences: "Object references",
      rowName: "Name",
      rowDescription: "Description",
      rowBranch: "Branch",
      rowViewKind: "View kind",
      rowUpdatedAt: "Last modified",
      empty: "empty",
      noRelations: "No relations.",
      noDiagrams: "Appears on no diagram (orphan).",
      noObjects: "Empty diagram — no object references.",
      loading: "Loading detail…",
      openButton: "Open",
      openTitle: "Open diagram on canvas",
      editHint: "Click to edit in the Properties panel",
      deleteObjectTitle: "Delete object (incl. all relations and diagram occurrences)",
      deleteButton: "Delete",
      doubleClickHint: "Double-click to open in the Properties panel",
      relationRowHint:
        "Click = select + focus canvas — double-click = also open attributes panel — drag = place on active diagram",
      collapseOccurrences: "Collapse diagram occurrences",
      expandOccurrences: "Expand diagram occurrences",
      showOccurrencesTitle: "Show diagrams this relation appears on",
      notPlaced: "Not placed on a diagram",
      instance: "Instance {{n}}",
      instanceTitle: "Instance {{n}} — {{diagram}}",
      attrChildHint: "Click = select; double-click = edit; drag = to diagram",
      kindIdentifier: "Identifier",
      kindAttribute: "Attribute",
      deleteDiagramConfirm: "Delete diagram '{{name}}'?",
      deleteDiagramFailed: "Deleting diagram failed — see console.",
      unknownViewKind: "{{viewKind}} (unknown)",
      deleteDiagramTitle: "Delete diagram",
      changeTypeTitle: "Change diagram type",
      selectObjectTitle: "Select object {{name}} in the tree",
    },
    pinned: "Pinned",
    pinPanel: "Pin panel",
    workpackageLabel: "Workpackage",
    branchLabel: "Branch",
    choose: "— choose —",
    closedSuffix: " (closed)",
    newBranchOption: "+ New branch…",
    search: {
      placeholder: "Search objects + diagrams…",
      ariaLabel: "Search explorer",
      clear: "Clear",
      clearAria: "Clear search field",
      noResults: 'No results for "{{query}}".',
    },
    selection: {
      selectedCount: "{{n}} selected",
      moveToFolder: "Move to folder…",
      deleteTitle: "Delete selected object(s) and diagram(s) — shortcut: Delete / Backspace",
      summary: "{{n}} items selected (multi-select)",
    },
    bulkDelete: {
      confirmDiagrams_one: "Delete {{count}} diagram?",
      confirmDiagrams_other: "Delete {{count}} diagrams?",
    },
    deleteFailed: "Delete failed — see console.",
    empty: {
      noWorkpackages:
        "No workpackages visible — ask the admin for READ/WRITE permissions or create a new workpackage.",
      pickWpBranch: "Choose a workpackage + branch to see the tree.",
      branchEmpty: "This branch is empty.",
    },
    item: {
      orphanTitle: "Orphan — appears on no diagram",
      tombstoneTitle: "Deleted on this branch",
      conceptHint: "Click = select; drag = to diagram; right-click = open in diagram",
      hideDetail: "Hide detail panel",
      showDetail: "Show detail panel",
      collapseDetail: "Collapse detail",
      expandDetail: "Expand detail",
      fromFolder: "From folder {{folder}} (Base)",
      referenceFromBase: "Reference object from Base",
      openInDiagram: "Open in diagram…",
      emptyDiagramTitle: "Empty diagram — no object references",
      diagramHint: "Click = select; double-click = open",
    },
    diagramsGroup: "[Diagrams]",
    branchOwnTitle: "Branch-owned on '{{branch}}'",
    openInDiagram: {
      title: "Open in diagram — {{name}}",
      none: "This object appears on no diagram.",
    },
    contextMenu: {
      label: "Actions",
      open: "Open",
      openInDiagram: "Open in diagram…",
      properties: "Properties",
      rename: "Rename",
      deleteDiagram: "Delete diagram…",
      deleteFromModel: "Delete from model…",
    },
  },
  foldertree: {
    multiSelect: {
      selectedIn_one: "{{count}} selected in {{folder}}",
      selectedIn_other: "{{count}} selected in {{folder}}",
      moveOutTitle: "Move out of folder…",
      deleteTitle: "Delete",
      clearTitle: "Clear selection",
      deleteConfirm:
        "Delete {{count}} concept(s)? This also removes their relations and diagram occurrences. Not undoable (use the Repository menu to snapshot first).",
      deletePartial: "{{ok}}/{{total}} deleted; {{failed}} failed.",
    },
    wpSelectTitle: "Choose workpackage",
    baseSuffix: " (Base)",
    title: "Foldertree (Base)",
    newFolder: "+ New folder",
    baseRoot: "Base",
    loadingFolders: "Loading folders…",
    noFolders: 'No folders created yet. Click "+ New folder" at the top.',
    footer: "Relations + Scratchpad canvas for cross-folder modelling: next iteration.",
    form: {
      name: "Name",
      namePlaceholder: "E.g. Customers",
      description: "Description (optional)",
      descriptionPlaceholder: "Short explanation of what this folder contains",
      typeClass: "Type class (fixed after creation)",
      submit: "Create folder",
    },
    editModal: {
      title: "Edit folder",
    },
    errorBar: {
      closeAria: "Close error message",
      close: "close",
    },
    filter: {
      searchPlaceholder: "Search folder…",
      label: "Filter:",
      all: "All",
    },
    folder: {
      collapseAria: "Collapse folder",
      expandAria: "Expand folder",
      typeClassTitle: "Type class: {{group}}",
      objCount: ", {{n}} obj",
      editTitle: "Edit name + description",
      exportTitle: "Export folder to JSON (outgoing)",
      exportAria: "Export",
      importTitle: "Import JSON into folder (incoming)",
      importAria: "Import",
      snapshotTitle: "Take a snapshot of this folder (folder-scoped)",
      snapshotAria: "Folder snapshot",
      empty: "Empty — drag a type from the palette here.",
      expandTypeGroupAria: "Expand type group",
      collapseTypeGroupAria: "Collapse type group",
      deleteConfirm:
        "Delete folder '{{name}}'?\n\nConcepts in it lose their «Reference Object» stereotype and stay behind in the Base workpackage.",
      snapshotSuccess:
        "✓ Snapshot of folder '{{name}}' created: {{concepts}} concept(s), {{relations}} relation(s). Find it under Settings → Snapshots → scope 'Base'.",
    },
    concept: {
      collapseAria: "Collapse concept",
      expandAria: "Expand concept",
      noRelations: "No relations",
      collapse: "Collapse",
      expand: "Expand",
      rowTitle: "{{typeId}}\n(click = select, double-click = open attributes panel)",
      rename: "Rename",
      deleteConfirm: "Delete concept '{{label}}'?",
    },
    relation: {
      rowTitle: "{{typeId}}\n{{typeLabel}}\n(double-click = open attributes panel)",
      deleteTitle: "Delete relation",
      deleteConfirm: "Delete relation '{{typeId}}'?",
    },
    import: {
      confirm:
        "Import '{{file}}' into folder '{{folder}}'? Existing objects with the same UUID are updated; new objects are added keeping their original UUID.",
      skippedNote: ", {{n}} relation(s) skipped",
      updatedNote: ", {{n}} updated",
      reassignedNote:
        "\n{{n}} object(s) got a new id because they already exist in another workspace.",
      success:
        "✓ {{inserted}} concept(s) added{{updatedNote}} + {{relations}} relation(s) imported{{skippedNote}}.{{reassignedNote}}",
    },
    errors: {
      loadFolders: "Loading folders failed: {{msg}}",
      createFolder: "Creating folder failed: {{msg}}",
      updateFolder: "Updating folder failed: {{msg}}",
      renameFolder: "Renaming folder failed: {{msg}}",
      deleteRelation: "Deleting relation failed: {{msg}}",
      renameConcept: "Renaming concept failed: {{msg}}",
      deleteConcept: "Deleting concept failed: {{msg}}",
      selectionClassMismatch:
        "Selection has type class '{{cls}}' and does not fit in folder '{{folder}}' ({{group}}).",
      moveToFolder: "Moving to '{{folder}}' failed: {{msg}}",
      moveSkipped: "Move skipped: {{reason}}",
      unknownReason: "unknown reason",
      paletteClassMismatch:
        "'{{typeId}}' falls under folder class '{{folderClass}}' but folder '{{folder}}' only accepts '{{group}}'.",
      createConcept: "Creating concept in '{{folder}}' failed: {{msg}}",
      export: "Folder export failed: {{msg}}",
      import: "Folder import failed: {{msg}}",
      deleteFolder: "Deleting folder failed: {{msg}}",
      snapshot: "Creating folder snapshot failed: {{msg}}",
    },
  },
  heatMap: {
    empty: "No concepts found",
    noEnumOptions:
      "The selected attributes have no enum options. Pick two enum attributes on the right panel.",
    notConfigured: {
      title: "Heat-map not yet configured",
      body: "Pick a (sub)type and two enum attributes on the right panel.",
    },
    noAxisValue: "No value on axis",
    v2: {
      notConfiguredTitle: "Heat map not configured yet",
      notConfiguredBody:
        "Select this diagram in the Explorer (left) and pick a target (sub)type plus two axes in the AttributePanel.",
      noAxisValues:
        "No values found on one of the axes. For numeric attributes: make sure there are concepts with a value.",
    },
    editNameTitle: "Double-click to edit the name",
    toolbar: {
      move: "Move toolbar (next corner)",
      zoomOut: "Zoom out (scroll wheel down)",
      resetZoom: "Reset to 100%",
      zoomIn: "Zoom in (scroll wheel up)",
    },
    dragReorderValues: "Drag to reorder the values",
    relation: {
      notConfiguredTitle: "Relation heatmap not configured yet",
      notConfiguredBody:
        "Open this diagram in the Diagrams panel or double-click the background. Then pick a relation stereotype, a relation attribute, a template stereotype, and a template attribute in the AttributePanel.",
      notEnumTitle: "Attributes not enum/numeric",
      notEnumBody:
        "Heatmap axes require enum or numeric attributes. Pick different attributes in the AttributePanel.",
      plotted: "relations plotted",
      hidden: "hidden — no template or value",
      diagonal: "diagonal reference",
    },
  },
  profiles: {
    hideDetails: "Hide details",
    intro:
      "A specialisation profile refines a standard type — shown with «guillemets». Profiles from a pack are maintained by that pack and are read-only here; self-defined profiles apply within this workpackage and can be edited here.",
    searchPlaceholder: "Search by name or label…",
    "new": "+ New profile",
    formNew: "New profile",
    formEdit: "Edit profile",
    fieldName: "Name",
    fieldNameHint:
      "The profile's identity — it is referred to by this everywhere. Pick a prefix, for example own.CoreApplication.",
    fieldRenameHint:
      "Renaming is allowed. Every object carrying this profile moves with it; only colour-overlay rules that mention the name need checking by hand.",
    confirmRename:
      'Rename profile "{{from}}" to "{{to}}"? Every object carrying it will be updated.',
    renameDone:
      'Renamed to "{{to}}". Updated: {{concepts}} concept(s), {{relations}} relation(s), {{diagrams}} diagram(s).',
    renameViewStateWarning_one:
      "Note: 1 diagram still mentions the old name in a colour-overlay rule — it was not updated automatically.",
    renameViewStateWarning_other:
      "Note: {{count}} diagrams still mention the old name in a colour-overlay rule — they were not updated automatically.",
    fieldLabelNl: "Label (NL)",
    fieldLabelEn: "Label (EN)",
    fieldAppliesTo: "Applies to",
    fieldAppliesToHint: "Multiple allowed. Without a base type the profile cannot be assigned.",
    fieldIcon: "Icon",
    colorClear: "Clear colour",
    fieldColors: "Colours",
    detailDescription: "Description",
    detailAttrKey: "Key",
    detailColorHint: "as it renders on the canvas",
    detailAttrLabel: "Label",
    detailRequired: "required",
    detailAttributes: "Attributes ({{count}})",
    detailAttrOptions: "Options",
    inspect: "Show details",
    detailAnyType: "any type",
    detailAttrType: "Type",
    detailNoAttributes: "This profile adds no fields.",
    colorText: "Text",
    colorStroke: "Border",
    colorFill: "Fill",
    fieldColorsHint:
      "Leaving these empty means: follow the base type's layer colour, as today. A profile that does have a colour beats that layer colour, but not a colour the DOM itself prescribes.",
    colorPreview: "Preview",
    fieldIconHint: "Pick from the bundled set. These icons follow the object's border colour.",
    iconNone: "— no custom icon —",
    sectionOwn: "Own profiles ({{count}})",
    sectionPacks: "From a pack ({{count}})",
    packsHint: "These are maintained by their pack and cannot be changed here.",
    noneOwn: "No own profiles in this workpackage yet.",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    loading: "Loading…",
    confirmDelete: 'Delete profile "{{name}}"?',
    loadFailed: "Could not load profiles.",
    saveFailed: "Could not save.",
    deleteFailed: "Could not delete.",
  },
  ameffReview: {
    title: "AMEFF import — confirm classification",
    summaryLabels_one: "{{count}} label",
    summaryLabels_other: "{{count}} labels",
    summaryDiagrams_one: "{{count}} diagram",
    summaryDiagrams_other: "{{count}} diagrams",
    summaryAnd: " and ",
    summarySuffix: " recognised. Adjust the typing below; only modified rows will be updated.",
    bulk: "Bulk:",
    bulkAllDrawing: "All → Drawing",
    bulkAllRisk: "All → Risk",
    bulkReset: "Reset to heuristic suggestion",
    colSource: "Source",
    colKind: "Kind",
    colName: "Name",
    colTypeStereotype: "Type / stereotype",
    colDiagramType: "Diagram type",
    chipAuto: "auto",
    chipAutoTitleLabel: "Suggestion via colour + keyword",
    chipAutoTitleView: "Suggestion via view-name match",
    chipNone: "—",
    chipNoneTitle: "No match",
    chipElement: "element",
    chipElementTitle: "Imported Label node → becomes a concept object",
    chipView: "view",
    chipViewTitle: "AMEFF view → becomes a diagram",
    sectionLabels: "Labels",
    sectionDiagrams: "Diagrams",
    sectionAmbiguous: "Multi-target conversions",
    summaryAmbiguous_one: "{{count}} multi-target conversion",
    summaryAmbiguous_other: "{{count}} multi-target conversions",
    ambiguousHint:
      "ArchiMate 4 allows more than one target type for these 3.x types. The chosen type is shown below; the specialisation profile stays unchanged and preserves the original identity.",
    colLegacyType: "3.x type",
    colTargetType: "Becomes",
    chipDefault: "default",
    chipDefaultTitle: "Automatically chosen target type — can be changed",
    defaultSuffix: " (default)",
    unknownSuffix: " (unknown)",
    close: "Close without changes",
    apply: "Apply",
    applying: "Working…",
    errorFallback: "Patch failed",
    choiceDrawing: "— None (Drawing)",
    choiceRisk: "Risk",
    choiceVulnerability: "Vulnerability",
    choiceThreatEvent: "Threat Event",
    choiceThreatActor: "Threat Actor",
    choiceLossEvent: "Loss Event",
    choiceControlObjective: "Control Objective",
    choiceSecurityPrinciple: "Security Principle",
    choiceCountermeasure: "Countermeasure",
    choiceAsset: "Asset",
  },
  setup: {
    title: "Set up a new installation",
    intro:
      "This installation has no administrator and no mail settings yet. Set them up here once; afterwards this page closes.",
    closed: "This installation has already been set up.",
    toLogin: "Go to sign-in",
    next: "Next",
    back: "Back",
    saved: "Saved.",
    saveAndNext: "Save and continue",
    claim: {
      code: "Setup code (from the install script or the log)",
      phrase: "Setup phrase",
      computing: "Computing…",
      expires: "Valid until {{date}}.",
    },
    refused: {
      already_used:
        "The setup code no longer works: something has already been set up. If that wasn't you, destroy the installation and start again.",
      expired: "The setup code has expired. Create a new one with ae-install reset-setup-code.",
      locked: "Too many wrong attempts. Create a new code with ae-install reset-setup-code.",
      not_configured:
        "The installation lacks SETTINGS_ENCRYPTION_KEY (or, for a setup phrase, SETUP_PHRASE_SALT).",
    },
    mail: {
      title: "1. Mail",
      intro:
        "Sign-in codes are sent with these settings. Use your organisation's SMTP server or a service such as Amazon SES, Azure Communication Services or Resend.",
      host: "SMTP server",
      port: "Port",
      username: "Username",
      password: "Password",
      fromAddress: "Sender (e-mail address)",
      fromName: "Sender (name, optional)",
      testTo: "Test mail to…",
      test: "Send test mail",
      testSent: "Test mail sent to {{to}}.",
      testHint: "The test mail only works after saving.",
    },
    admin: {
      title: "2. Administrator and first workspace",
      intro:
        "The super admin manages the installation and is admin of the first workspace. That address will receive a sign-in code shortly.",
      email: "Super admin e-mail address",
      workspaceName: "Name of the first workspace",
      language: "Default language",
    },
    ai: {
      title: "3. Claude (optional)",
      intro:
        'Enter an Anthropic API key for translation and "Ask Claude", or skip this. Set up Workload Identity Federation later under Settings → Workspace → AI.',
      skip: "Skip",
    },
    confirm: {
      title: "4. Confirm",
      intro: "A sign-in code was sent to {{email}}. Enter it to finish the setup.",
      resend: "Send again",
      resent: "New code sent.",
      finish: "Finish and sign in",
    },
  },
  settings: {
    title: "Settings",
    backToEditor: "← Back to editor",
    common: {
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      delete: "Delete",
      edit: "Edit",
      create: "Create",
      creating: "Creating…",
      refresh: "↻ Refresh",
      noWorkspace: "No workspace loaded.",
      unknownError: "Unknown error",
      close: "Close",
      loading: "Loading…",
    },
    tabs: {
      account: "Account",
      diagramTypes: "Diagram types",
      subtypes: "Subtypes",
      profiles: "Specialisation profiles",
      validation: "Validation",
      portability: "Export / Import",
      email: "Email",
      quotas: "Quotas",
      workpackages: "Workpackages",
      teams: "Teams",
      branches: "Branches",
      snapshots: "Snapshots",
      trash: "Trash",
      audit: "Audit log",
      maintenance: "Maintenance",
      superAdmin: "Workspace",
      apiTokens: "API tokens",
      ai: "AI",
      globalMail: "E-mail (global)",
    },
    account: {
      aiNote:
        'Signing in to the AI provider (for translation and "Ask Claude") is set up for the whole installation by a super admin, under Settings → Workspace → AI.',
      aiModelSectionTitle: 'AI model for "Ask Claude"',
      aiModelDescription:
        "Which Claude model is used when you ask Claude to redesign a diagram. Stronger models give better quality but are slower and more expensive.",
      modelLabel: "Model",
      modelDefault: "Default",
      modelRecommended: "recommended",
      modelSaved: "AI model preference saved: {{label}}",
      modelReset: "AI model preference reset to default.",
      effortLabel: "Thinking",
      effortDefault: "Default (Balanced)",
      effortLow: "Fast: thinks briefly, for simple questions",
      effortMedium: "Balanced",
      effortHigh: "Thorough: thinks longest, for difficult redesigns",
      effortDescription:
        "How long Claude thinks before answering in Ask Claude. Shorter is faster and cheaper; you can still change it per question in the dialog.",
      effortSaved: "Thinking preference saved.",
    },
    aiAuth: {
      provider: "Provider: {{name}}.",
      providerFuture:
        "Other providers (such as OpenAI) are not built yet; contributions are welcome.",
      title: "Signing in to Anthropic",
      intro:
        'Translation, "Ask Claude" and the colour overlay use Claude. Choose how this installation signs in to the AI provider; it applies to all workspaces. Workload Identity Federation is preferred: there is no long-lived key. If both are set, the editor falls back to the key when federation fails.',
      wifTitle: "Workload Identity Federation",
      wifIntro:
        "Copy these three values into the Claude Console (Settings › Workload identity): register the editor as an issuer and create a federation rule with exactly this subject. Then fill in the IDs below.",
      wifUnavailable:
        "This installation cannot sign tokens yet: the operator must set WIF_SIGNING_KEY and PUBLIC_ORIGIN. Until then only an API key works.",
      issuer: "Issuer URL",
      jwksUrl: "JWKS URL",
      subject: "Subject",
      copy: "Copy",
      copied: "Copied",
      organizationId: "Organization ID",
      serviceAccountId: "Service account ID (svac_…)",
      federationRuleId: "Federation rule ID (fdrl_…)",
      anthropicWorkspaceId: "Anthropic workspace ID (wrkspc_…, optional)",
      anthropicWorkspaceHint: "Only needed when the rule covers several Anthropic workspaces.",
      saveWif: "Save WIF",
      clearWif: "Turn WIF off",
      wifSet: "Configured",
      keyTitle: "API key",
      keyIntro:
        "A key from the Claude Console. It is stored encrypted; afterwards you only see the last four characters.",
      keySet: "Configured, ends in {{last4}}",
      keyUnset: "No key",
      newKey: "New key",
      keyPlaceholder: "sk-ant-…",
      keyWorkspace: "Anthropic workspace ID (wrkspc_…, optional)",
      keyWorkspaceHelp:
        'Only needed for a key that is not scoped to one Anthropic workspace: Anthropic then answers "This API key is not scoped to a workspace". Enter the ID (starts with wrkspc_), not the name; you find it in the Claude Console under Settings › Workspaces, on the workspace itself.',
      saveKey: "Save key",
      clearKey: "Clear key",
      test: "Test",
      testing: "Testing…",
      testOk: "Works ({{mode}}).",
      testFailed: "Failed: {{detail}}",
      modeWif: "Workload Identity Federation",
      modeKey: "API key",
      modeDevKey: "local key from .dev.vars",
      lastFailure: "Last failure ({{date}}): {{reason}}",
      saved: "Saved.",
      notConfigured:
        "No sign-in to an AI provider is configured. A super admin sets it up under Settings → Workspace → AI.",
    },
    diagramTypes: {
      title: "Diagram types",
      description:
        "Workspace-specific diagram types you can use alongside the standard Open Group viewpoints. Any workspace member can create one; the creator (or a workspace admin) can edit or delete it.",
      newButton: "+ New type",
      emptyHintBefore: "No custom diagram types yet. Click ",
      emptyHintCta: "+ New type",
      emptyHintAfter: " to create one.",
      centrallyManagedBadge: "centrally managed",
      conceptTypesCount_one: "{{count}} concept type",
      conceptTypesCount_other: "{{count}} concept types",
      relationTypesCount_one: "{{count}} relation type",
      relationTypesCount_other: "{{count}} relation types",
      creatorLabel: " · creator ",
      editTitleEnabled: "",
      editTitleManaged: "Centrally managed — admins only",
      editTitleOther: "Only the creator or an admin can edit this type",
      deleteConfirm: "Delete diagram type '{{name}}'? Diagrams using it will revert to 'Anything'.",
      editorTitleCreate: "New diagram type",
      editorTitleEdit: "Edit diagram type",
      nameNl: "Name (NL)",
      nameEn: "Name (EN)",
      descriptionNl: "Description (NL)",
      descriptionEn: "Description (EN)",
      allowedConceptTypes: "Allowed concept types ({{count}})",
      allowedRelationTypes: "Allowed relation types ",
      relationSubset: "(subset of {{count}})",
      relationAllAllowed: "(all matrix-allowed)",
      restrictRelationTypes: "Restrict relation types",
      centrallyManagedLabel: "Centrally managed",
      centrallyManagedHint: " — when checked, only workspace admins can edit or delete this type.",
      validationNameRequired: "Enter at least one name (NL or EN).",
      validationTypesRequired: "Select at least one concept type.",
      translating: "translating…",
    },
    validation: {
      summary: "{{fields}} fields on {{objects}} objects still need a value.",
      description:
        "All required fields that still carry a TBD/NTB placeholder in the active branch. Fill them in to complete the model.",
      empty: "No outstanding TBD/NTB fields — everything is filled in.",
      jumpHint: "Click to open this object in the properties panel",
      col: {
        object: "Object",
        type: "Type / stereotype",
        field: "Field",
        value: "Value",
        lastEdit: "Last edited",
      },
    },
    subtypes: {
      title: "Subtypes",
      descriptionBefore:
        'Workspace-specific specializations — for example "PaymentService" as a subtype of ',
      descriptionAfter:
        ". The subtype inherits the full attribute schema from its parent. Any workspace member can create one; the creator or a workspace admin can edit or delete it.",
      newButton: "+ New subtype",
      emptyHintBefore: "No subtypes yet. Click ",
      emptyHintCta: "+ New subtype",
      emptyHintAfter: " to create one.",
      extendsLabel: "extends {{parent}}",
      byCreator: "By {{email}}",
      extraAttributesCount_one: " · {{count}} extra attribute",
      extraAttributesCount_other: " · {{count}} extra attributes",
      deleteConfirm:
        'Delete subtype "{{name}}"?\n\nExisting concepts of this subtype will revert to the parent type "{{parent}}". Their data is preserved, but the subtype-specific attributes will be lost.',
      deleteFailed: "Delete failed",
      createFailed: "Create failed",
      formTitle: "New subtype",
      parentTypeLabel: "Parent type",
      nameNl: "Name (NL)",
      nameEn: "Name (EN)",
      nameNlPlaceholder: "Betalingsservice",
      nameEnPlaceholder: "Payment service",
      descriptionNl: "Description (NL)",
      descriptionEn: "Description (EN)",
      inheritsNote:
        "The subtype automatically gets all attributes from the parent type. The attributes added below come on top of those.",
      validationParentRequired: "Choose a parent type.",
      validationNamesRequired: "Enter both a Dutch and an English name.",
      formTitleEdit: "Edit subtype",
      saveFailed: "Save failed.",
      parentImmutable: "The parent type cannot be changed after creation.",
      attrsLabel: "Own extra attributes",
      attrsHint:
        "On top of the parent type's attributes. The key is machine-readable (e.g. sla_level); the label is what the modeller sees.",
      attrName: "key",
      attrLabelNl: "Label (NL)",
      attrLabelEn: "Label (EN)",
      attrType: {
        string: "Text (short)",
        bilingual_string: "Text NL/EN",
        int: "Number",
        bool: "Yes/no",
        date: "Date",
        enum: "Dropdown",
      },
      attrRequired: "Required",
      attrOptions: "options, comma-separated",
      attrRemove: "Remove",
      attrAdd: "+ Add attribute",
      validationAttr: "Each attribute needs a key and at least one label.",
      validationAttrOptions: 'Dropdown "{{name}}" needs at least one option.',
    },
    portability: {
      introTitle: "Export / Import",
      introBefore:
        " moves a full model (concepts, relations, diagrams) between workspaces or installations: as JSON, or as AMEFF (Open Group ArchiMate Model Exchange File Format, .xml) for exchange with other ArchiMate tools.",
      introDetail1:
        "A JSON import keeps an object's id when it is free. If the id already exists on the target branch, that object is updated; if it is in use elsewhere (in this or another workspace), the object gets ",
      introDetailNewIds: "a new id",
      introDetailAnd:
        '. A new object whose name already exists gets "[imported]" after its name, and you become the ',
      introDetailOwner: "owner",
      introDetailAfter:
        " of everything you import. An AMEFF import always creates new objects with new ids.",
      exportTitle: "Export",
      exportDescription:
        "Download live concepts, relations and diagrams as a JSON file. 'Entire workspace': all concepts and relations of the workspace plus the diagrams of the branch you are on. 'One workpackage': that workpackage's content (your current branch if you work in it, otherwise its main branch). Deleted items and version history are not included.",
      exportButton: "Download JSON",
      exportSuccess: "'{{filename}}' downloaded ({{size}}).",
      importTitle: "Import",
      importDescription:
        "Upload a previously exported JSON file. Its content goes into the current branch: objects whose id already exists there are updated, the rest is added.",
      importButton: "Choose JSON file…",
      importConfirm:
        "Import '{{filename}}' ({{size}}) into the current branch? Objects whose id already exists there are updated; the rest is added next to the existing data.",
      importInvalidJson: "File is not valid JSON.",
      importedSuccess: "Imported: {{parts}}.{{safetyNote}}",
      importConceptsPart: "{{total}} concepts ({{inserted}} new, {{updated}} updated)",
      importRelationsPart: "{{total}} relations ({{inserted}} new, {{updated}} updated)",
      importDiagramsPart: "{{total}} diagrams ({{inserted}} new, {{updated}} updated)",
      importCustomViewKindsPart: "{{total}} custom diagram types",
      importSkippedRelations: "{{count}} relations skipped (dangling endpoint)",
      importSafetyNote:
        " A snapshot '{{label}}' was created automatically before import — restorable via the Snapshots tab if needed.",
      reviewTitle_one: '⚠ {{count}} object marked with "[imported]"',
      reviewTitle_other: '⚠ {{count}} objects marked with "[imported]"',
      reviewDescription:
        "These objects were newly created and their name matched an existing object — `[imported]` suffix added. They may be duplicates. Click a name to edit it, or remove the suffix in the Properties panel. Is it the same object? Remove the suffix and merge the two: right-click → Merge on the diagram, or Repository → Deduplicate all objects.",
      reviewLabelConcepts: "Concepts",
      reviewLabelRelations: "Relations",
      reviewLabelDiagrams: "Diagrams",
      reviewLabelCustomViewKinds: "Custom diagram types",
      countConcepts_one: "{{count}} concept",
      countConcepts_other: "{{count}} concepts",
      countRelations_one: "{{count}} relation",
      countRelations_other: "{{count}} relations",
      countDiagrams_one: "{{count}} diagram",
      countDiagrams_other: "{{count}} diagrams",
      countCustomViewKinds_one: "{{count}} custom diagram type",
      countCustomViewKinds_other: "{{count}} custom diagram types",
      countDrawings_one: "{{count}} visual primitive (decoration on diagrams)",
      countDrawings_other: "{{count}} visual primitives (decoration on diagrams)",
      exportSuccessWithCounts: "'{{filename}}' downloaded ({{size}}){{counts}}.",
      errorNoTargetBranch: "No target workpackage selected, or the WP has no main branch.",
      currentWp: "current WP",
      ameffImportConfirm:
        "Import AMEFF file '{{filename}}' ({{size}}) into workpackage {{target}}? AMEFF import always creates fresh UUIDs; a pre-import snapshot is NOT created automatically (create one yourself via the Snapshots tab if you want a safety net).",
      importCancelled: "Import cancelled — nothing was changed.",
      ameffConcepts: "{{n}} concepts",
      ameffRelations: "{{n}} relations",
      ameffDiagrams: "{{n}} diagrams",
      ameffConceptsSkipped: "{{n}} concept(s) skipped",
      ameffRelationsSkipped: "{{n}} relation(s) skipped",
      ameffDiagramsSkipped: "{{n}} diagram(s) skipped",
      ameffRelationsToCheck: "{{n}} relation(s) to check",
      unknownElementTypes: "unknown element types: {{types}}",
      unknownRelationshipTypes: "unknown relationship types: {{types}}",
      zeroItems: "0 items",
      ameffImportSuccess: "AMEFF import into {{target}}: {{parts}}{{tail}}.",
      importConfirmTarget:
        "Import '{{filename}}' ({{size}}) into workpackage {{target}}? Objects whose id already exists there are updated; the rest is added next to the existing data.",
      importConceptsPartReused:
        "{{total}} concepts ({{inserted}} new, {{updated}} updated, {{reused}} reused (folder match))",
      foldersCreated: "{{n}} folder(s) created automatically in Base",
      importedSuccessTarget: "Imported into {{target}}: {{parts}}.{{safetyNote}}",
      scopeLegend: "Scope",
      scopeWorkspace: "Entire workspace",
      scopeWorkpackage: "One workpackage",
      wpScopeHint:
        "Folder objects this workpackage subscribes to are included inline with their folder name. On import into another workspace they are matched by folder + name + type — existing Reference Objects are reused (UUID link).",
      ameffExportTitle:
        "Open Group ArchiMate Model Exchange File Format — exchange with Archi, BiZZdesign, etc. Follows the scope choice: 'Entire workspace' exports the branch you are on, 'One workpackage' the main branch of the chosen workpackage.",
      ameffExportButton: "Download AMEFF (XML)",
      importDescriptionTarget:
        "Upload a previously exported JSON file and choose the workpackage it lands in. Objects whose id already exists there are updated; the rest is added.",
      targetWpLabel: "Target workpackage:",
      folderMatchHint:
        "Folder objects in the file (with the «Reference Object» stereotype + folder name) are matched by name + type in the Base WP. Existing matches → reused; new ones → placed in the folder; folder missing in target → stereotype stripped, imported as a regular concept in this WP.",
      ameffImportTitle:
        "Open Group ArchiMate Model Exchange File Format — exchange with Archi, BiZZdesign, etc.",
      ameffImportButton: "Choose .archimate file (AMEFF)…",
      suffixHeading_one: '{{count}} object marked with "[imported]"',
      suffixHeading_other: '{{count}} objects marked with "[imported]"',
      suffixBody:
        "These objects were newly created and their name matched an existing object in the target WP — `[imported]` suffix added. Possibly duplicates. Click a name to edit it, or remove the suffix in the Properties panel. Is it the same object? Remove the suffix and merge the two: right-click → Merge on the diagram, or Repository → Deduplicate all objects.",
      uuidHeading_one: "{{count}} object received a fresh UUID",
      uuidHeading_other: "{{count}} objects received a fresh UUID",
      uuidBody:
        "These objects had a UUID in the import file that is already in use elsewhere (in this or another workspace). A fresh UUID was assigned to avoid PRIMARY KEY conflicts — the name is unchanged, no action required. Relations + parent pointers automatically point to the new UUIDs.",
    },
    mail: {
      introTitle: "Two transport options:",
      mcLabel: "MailChannels",
      mcDescriptionBefore: " — free HTTP relay, no credentials required. ",
      mcWel: "Does",
      mcDescriptionAfter: " require a DNS TXT record on the from-domain (",
      mcDescriptionEnd: ") so MailChannels knows this worker may send on behalf of your domain.",
      smtpLabel: "SMTP",
      smtpDescriptionBefore:
        " — direct connection to your own SMTP server via Cloudflare Workers' TCP socket API. Fill in ",
      smtpHost: "host",
      smtpComma: ", ",
      smtpPort: "port",
      smtpUsername: "username",
      smtpEn: " and ",
      smtpPassword: "password",
      smtpDescriptionAfter:
        ". Port 465 = implicit TLS; all other ports (typically 587) use STARTTLS.",
      fieldTransport: "Transport",
      fieldFromAddress: "From address",
      fieldFromName: "From name",
      fieldSmtpHost: "SMTP host",
      fieldPort: "Port",
      fieldSmtpUsername: "SMTP username",
      fieldSmtpPassword: "SMTP password",
      passwordSet: " (already saved)",
      passwordPlaceholderSet: "•••••••• (leave empty to keep)",
      transportMailchannels: "MailChannels (HTTP relay)",
      transportSmtp: "SMTP (own server, direct connection)",
      fromAddressPlaceholder: "noreply@yourcompany.com",
      fromNamePlaceholder: "Arch-designer",
      hostPlaceholder: "smtp.gmail.com",
      usernamePlaceholder: "(optional)",
      savedToast: "Saved.",
      removedToast: "Mail config removed.",
      removeConfirm: "Clear mail config for this workspace?",
      testButton: "Send test email",
      testButtonTitleConfigured: "Test email to your own address",
      testButtonTitleNotConfigured: "Save first",
      testSuccess: "Test email sent to {{recipient}}.",
      clearConfig: "Clear config",
      saveButton: "Save",
      updatedBy: "Last updated by {{user}} on {{date}}.",
    },
    quotas: {
      sectionTitle: "Daily limit for Ask Claude",
      defaultLimitBefore: "Default limit:",
      defaultLimitDetail: "{{count}} 'Ask Claude' requests per day",
      defaultLimitAfter: ". Resets at 00:00 UTC. Override per user below.",
      userEmailLabel: "User (email)",
      userEmailPlaceholder: "user@company.com",
      dailyLimitLabel: "Daily limit",
      addOverrideButton: "+ Override",
      emptyHint: "No overrides yet — all users use the default limit.",
      colUser: "User",
      colKind: "Kind",
      colLimit: "Limit",
      colChanged: "Changed",
      errorInvalidEmail: "Not a valid email address.",
      errorInvalidLimit: "Limit must be a non-negative integer.",
      toastReset: "{{email}}: back to default.",
      toastSaved: "{{email}}: {{limit}}/day saved.",
      saveButton: "Save",
      removeOverrideTitle: "Remove override (back to default)",
    },
    workpackages: {
      subtabWorkpackages: "Workpackages",
      subtabBranches: "Branches",
      subtabFolderPermissions: "Folder permissions",
      subtabTeamPermissions: "Team permissions",
      subtabCopy: "Copy",
      newTitle: "New workpackage",
      newDescription:
        "A workpackage groups branches, concepts and diagrams under one banner — a project, a business line, a specific theme. Teams can be authorized per workpackage (see Team permissions).",
      nameLabel: "Name",
      namePlaceholder: "e.g. EDM, Customer Project Q3",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "What is the scope of this workpackage?",
      createButton: "Create",
      createdToast: "Workpackage '{{name}}' created.",
      listTitle: "Workpackages",
      listDescription:
        "Click a row to edit name or description. Closing keeps the workpackage available but marks it as closed; deleting removes everything (irreversible).",
      empty: "No workpackages yet.",
      baseBadge: "base",
      baseBadgeTitle:
        "Special workpackage with shared reference objects — admin-only and not editable.",
      closedBadge: "closed",
      createdByOn: "created by {{user}} on {{date}}",
      editPlaceholderName: "Name",
      editPlaceholderDescription: "Description",
      saveButton: "Save",
      editButton: "Edit",
      editTitleBase: "Base workpackage is immutable",
      editTitleEnabled: "Edit name + description",
      closeButton: "Close",
      reopenButton: "Reopen",
      toggleTitleBase: "Base workpackage cannot be closed",
      toggleTitleReopen: "Reopen this workpackage",
      toggleTitleClose: "Close this workpackage",
      updatedToast: "Workpackage updated.",
      deletedToast: "Workpackage '{{name}}' deleted.",
      statusToastOpen: "Workpackage '{{name}}' is now open.",
      statusToastClosed: "Workpackage '{{name}}' is now closed.",
      deleteButton: "Delete",
      deleteTitleBase: "Base workpackage cannot be deleted",
      deleteTitleEnabled: "Delete workpackage (cascades to branches/concepts/diagrams)",
      deleteConfirm:
        "Are you sure you want to delete workpackage '{{name}}'?\n\nThis removes ALL branches, concepts, relations and diagrams that live in this workpackage. Cross-workpackage copy source references pointing at it will also be wiped.\n\nThis action cannot be undone.",
      deleteToast: "Workpackage '{{name}}' deleted.",
      folderSectionTitle: "Folder subscriptions per workpackage",
      folderSectionDescriptionBefore: "Grant a workpackage ",
      folderSectionRead: "read",
      folderSectionAnd: " or ",
      folderSectionWrite: "write",
      folderSectionDescriptionMid:
        " access to a folder in the Base workpackage. At most one workpackage may hold ",
      folderSectionDescriptionEnd:
        " on a folder; others read along. Workpackages without a subscription don't see the folder.",
      folderEmptyNoWps: "Create a (non-Base) workpackage first to manage subscriptions.",
      folderEmptyNoFolders: "No folders in the Base workpackage yet.",
      workpackageSelect: "Workpackage",
      folderColFolder: "Folder",
      folderColSubscription: "Subscription",
      folderColWriteHolder: "Write holder",
      folderThisWp: "(this WP)",
      folderRevoked: "Subscription revoked.",
      folderSet: "Folder access set to '{{mode}}'.",
      folderWriteHeldBy: "Write is held by '{{name}}'",
      teamSectionTitle: "Team permissions per workpackage",
      teamSectionDescriptionBefore: "Grant a team ",
      teamSectionDescriptionMid: " or ",
      teamSectionDescriptionEnd:
        " access to a workpackage. The Admin team is excluded — admins get full access through their role.",
      teamEmptyNoWps: "No open workpackages to manage.",
      teamEmptyNoTeams:
        "No teams created (other than Admin). Create a team in the Teams tab first.",
      teamColTeam: "Team",
      teamColAccess: "Access",
      teamRevoked: "Access revoked.",
      teamSet: "Team permission set to '{{mode}}'.",
      copyTitle: "Copy across workpackages",
      copyNeedTwo: "Create at least two open (non-Base) workpackages to copy concepts.",
      copyDescription:
        "Copies selected concepts and diagrams from the main branch of the source WP to the main branch of the target WP. Relations between selected concepts are copied automatically. Folder objects are skipped — manage those via folder subscriptions. ViewState references in diagrams that are not in the selection are stripped (no dangling refs).",
      copySourceLabel: "Source workpackage",
      copyTargetLabel: "Target workpackage",
      copyModeLabel: "Match mode",
      copyModeMatch: "Reuse on name+type match",
      copyModeMatchDesc:
        " — concepts with the same type id and name (NL or EN) in the target WP are reused; the rest is created new.",
      copyModeAlwaysNew: "Always new",
      copyModeAlwaysNewDesc:
        " — every concept gets a fresh UUID in the target WP, even when there is a name match. (Folder objects remain skipped.)",
      copySourceContent: "Source WP content",
      copyClearSelection: "Clear selection",
      copyObjects: "Objects",
      copyDiagrams: "Diagrams",
      copySelectionHint:
        "{{objects}} object(s) · {{diagrams}} diagram(s) selected. Diagram selection automatically expands to all objects and relations on that diagram. Object selection copies relations between selected objects.",
      copyDryRun: "Dry run (preview)",
      copyRun: "Copy",
      copyResult: "Result",
      copyResultDryRun: " (dry run)",
      copyColPass: "Pass",
      copyColReused: "Reused",
      copyColCreated: "New",
      copyColSkipped: "Skipped",
      copyRowConcepts: "Concepts",
      copyRowRelations: "Relations",
      copyRowDiagrams: "Diagrams",
      copyDetails: "Per-item details",
      copyDetailUnnamed: "(unnamed {{kind}})",
      copyErrorSame: "Source and target must differ.",
      copyErrorEmpty: "Select at least one concept or diagram.",
      copyDoneDryRun: "Dry run — {{summary}} (created+reused).",
      copyDoneRun: "Done — {{summary}} (created+reused).",
      explorerEmpty: "No {{title}} in source WP.",
      explorerLoading: "Loading source WP…",
      explorerTombstoned: "deleted",
      layerDrawing: "Drawing",
      copySummary:
        "concepts {{cc}}+{{cr}}={{ct}} · relations {{rc}}+{{rr}}={{rt}} · diagrams {{dc}}+{{dr}}={{dt}}",
    },
    teams: {
      newTeamTitle: "New team",
      newTeamDescription:
        "A team groups users for workpackage permissions. A founding member who is not yet a member of the workspace is added to it automatically, and gets an email about the team.",
      nameLabel: "Name",
      namePlaceholder: "e.g. Architects, Solution design",
      founderLabel: "Founding member (email)",
      founderPlaceholder: "name@company.com",
      descriptionLabel: "Description (optional)",
      createButton: "Create team",
      createdToast: "Team '{{name}}' created.",
      listTitle: "Teams",
      listDescriptionBefore: "Click a row to manage members. Members of the ",
      listDescriptionAdminTeam: "Admin team",
      listDescriptionAfter:
        " automatically get admin rights on the workspace; that team is auto-created and cannot be renamed or deleted.",
      empty: "No teams created yet.",
      adminBadge: "admin",
      adminBadgeTitle:
        "Members automatically get admin rights on this workspace. Auto-created and immutable.",
      toggleMembersHide: "Hide members",
      toggleMembersShow: "Members",
      toggleMembersTitleHide: "Hide members",
      toggleMembersTitleShow: "Show and manage members",
      deleteButton: "Delete",
      deleteTitleAdmin: "The Admin team cannot be deleted",
      deleteTitleOther: "Delete team + members + permissions",
      deleteConfirm: "Delete team '{{name}}'? Members + permissions are removed too.",
      deletedToast: "Team '{{name}}' deleted.",
      noMembers: "No members.",
      memberRemove: "remove",
      memberEmailPlaceholder: "email@company.com",
      addMember: "+ Member",
      addedAdmin: "'{{email}}' added — gets admin rights.",
      addedRegular: "'{{email}}' added to team '{{name}}'.",
      removeConfirm: "Remove '{{email}}' from team '{{name}}'?",
      removedAdmin: "'{{email}}' removed — admin rights revoked.",
      removedRegular: "'{{email}}' removed from team.",
    },
    branches: {
      wpPickerTitle: "Workpackage",
      wpPickerEmpty: "No open workpackages available.",
      wpPickerBaseMarker: "Base",
      introBefore: "",
      introBoldTitle: "Branches",
      introAfter:
        " are parallel versions of the diagrams in this workspace. Concepts and relations are shared; diagrams are per branch.",
      introStatuses1: "A branch has one of three statuses: ",
      statusActive: "active",
      statusActiveLong: " (editable), ",
      statusMerged: "merged",
      statusMergedLong: " (completed, history), ",
      statusAbandoned: "abandoned",
      statusAbandonedLong:
        " (stopped). The main branch of a workpackage is always active and cannot be renamed or deleted.",
      newSectionTitle: "Create a new branch",
      nameLabel: "Name",
      namePlaceholder: 'e.g. "feature/api-redesign"',
      sourceLabel: "Source",
      sourceFromBranch: "From existing branch:",
      sourceFromSnapshot: "From snapshot:",
      createButton: "Create branch",
      createdToast: "Branch '{{name}}' created.",
      empty: "No branches yet.",
      currentBadge: "current",
      colName: "Name",
      colStatus: "Status",
      colSource: "Source",
      colCreated: "Created",
      colCreatedBy: "By",
      colActions: "Actions",
      sourceSnapshotMarker: "(snapshot)",
      renamePrompt: "Rename branch '{{name}}' to:",
      renamedToast: "Branch renamed to '{{name}}'.",
      renameButton: "Name",
      renameTitleMain: "Main branch may not be renamed",
      renameTitleEnabled: "Rename",
      mergedButton: "Merged",
      mergeButton: "⇥ Merge into {{parent}}",
      mergeButtonTitle: "Take the content of this branch into its parent",
      deleteMergeHint:
        "\n\nWant to keep the plateaus below? Use 'Merge' instead: the content goes into the parent and the branches below get that as parent.",
      abandonButton: "⊘ Abandon",
      reactivateButton: "Reactivate",
      deleteButton: "Delete",
      deleteButtonTitle: "Delete branch + diagrams",
      statusConfirmAbandoned:
        "Change status of '{{name}}' to '{{status}}'? This branch will become read-only.",
      statusConfirmMerged:
        "Change status of '{{name}}' to '{{status}}'? Mark as merged — read-only but kept as history.",
      statusConfirmActive:
        "Change status of '{{name}}' to '{{status}}'? Reactivate for further editing.",
      statusToast: "Status of '{{name}}' is now '{{status}}'.",
      deleteConfirm:
        "Permanently delete branch '{{name}}'? Everything created or changed on this branch (objects, relations, diagrams) and its snapshots is removed; what it inherited stays where it lives. This CANNOT be undone.",
      deleteConfirmSubtree:
        "Permanently delete branch '{{name}}' together with the branches below it?\n\n{{list}}\n\nEverything created or changed on these branches is removed, with their snapshots and their plateau on the roadmap. This CANNOT be undone.",
      deletedToast: "Branch '{{name}}' deleted.",
      badgeActive: "active",
      badgeMerged: "merged",
      badgeAbandoned: "abandoned",
    },
    snapshots: {
      introBefore: "",
      introBoldTitle: "Snapshots",
      introAfter:
        " preserve the full workspace state (concepts, relations, diagrams) as one point in time. Useful before large refactorings or as a milestone checkpoint.",
      introDetail:
        "A restore reverts all live data; objects added later are tombstoned (visible in the Trash). Before each restore a safety snapshot is created automatically — rolling back is always possible.",
      newSectionTitle: "Create a new snapshot",
      labelLabel: "Label",
      labelPlaceholder: 'e.g. "Before {{date}} refactor"',
      messageLabel: "Message (optional)",
      messagePlaceholder: "Free text — context for future restore decisions.",
      createButton: "Create snapshot",
      createdToast:
        "Snapshot '{{label}}' created — {{concepts}} concepts, {{relations}} relations, {{diagrams}} diagrams, {{viewStates}} viewState entries.",
      reusedToast: "Nothing has changed since snapshot '{{label}}' — no new one created.",
      statusLater: "Cannot be restored right now.",
      statusNever: "Can no longer be restored.",
      structureIntro: "This snapshot also restores the plateaus below it to how they were:",
      branchRemoved: "• '{{name}}' is removed (did not exist yet)",
      branchRecreated: "• '{{name}}' comes back",
      showAllBranches: "Show snapshots from all branches",
      scopeLabel: "Scope:",
      scopeWorkspace: "Workspace (workspace-wide snapshots)",
      scopeBaseMarker: "Base",
      empty: "No snapshots yet — create one above.",
      colLabel: "Label",
      colWhen: "When",
      colWho: "By",
      colContent: "Content",
      colSize: "Size",
      colActions: "Actions",
      restoreButton: "Restore",
      restoreTitle: "Restore the workspace to this snapshot",
      restoreConfirm:
        "Restore snapshot '{{label}}'? All concepts/relations/diagrams not in the snapshot will be tombstoned (recoverable via the Trash). A safety snapshot is created automatically beforehand.",
      restoreConfirmBranch:
        "Restore snapshot '{{label}}'? The plateau and the plateaus below it go back to how they were; what was created on them since is removed. Only these plateaus are locked briefly. A safety snapshot is created automatically beforehand.",
      restoreCancelled: "Restore called off — nothing changed.",
      restoredToast:
        "'{{label}}' restored. Concepts: +{{ci}} ~{{cu}} -{{ct}}. Relations: +{{ri}} ~{{ru}} -{{rt}}. Diagrams: ~{{dr}} -{{dx}}. Safety snapshot: '{{safety}}'.",
      jsonButton: "JSON",
      jsonTitle: "Open the JSON content in a new tab",
      deleteButton: "Delete",
      deleteTitle: "Delete permanently",
      deleteConfirm:
        "Permanently delete snapshot '{{label}}'? The R2 blob and metadata are discarded. This cannot be undone.",
      deletedToast: "Snapshot '{{label}}' deleted.",
      reloadHint: "The page reloads automatically as soon as you close this window.",
    },
    trash: {
      selectGroup: "Select everything from {{when}}",
      cleanupTitle: "Clean up permanently",
      refresh: "Refresh",
      introBefore: "",
      introBoldTitle: "Trash",
      introAfter:
        " contains deleted concepts and relations. Until they are permanently removed, you can restore them.",
      introDetail:
        "The automatic cron job does not clean up deleted concepts — those stay here until an admin permanently deletes them. Orphan relations (source/target hard-deleted) are removed automatically.",
      restoreButton: "Restore ({{count}})",
      purgeSelectionButton: "Delete permanently ({{count}})",
      olderThanLabel: "Items older than",
      purgeOlderButton: "Delete permanently",
      purgeAllButton: "Empty trash",
      purgeAllTitle: "Permanently delete every item in this trash",
      empty: "The trash is empty.",
      itemsCount_one: "{{count}} item",
      itemsCount_other: "{{count}} items",
      kindConcept: "Concept",
      kindRelation: "Relation",
      noName: "(no name)",
      noLabel: "(no label)",
      placedOn: "Placed on",
      expandGroup: "Show items in this delete action",
      collapseGroup: "Hide items in this delete action",
      danglingBadge: "Endpoint also deleted",
      danglingTitle:
        "Source or target of this relation is also in the trash — restoring requires both.",
      restoreEmpty: "Nothing restored.",
      restoreSuccess: "Restored: {{parts}}.",
      restoreConcepts_one: "{{count}} concept",
      restoreConcepts_other: "{{count}} concepts",
      restoreRelations_one: "{{count}} relation",
      restoreRelations_other: "{{count}} relations",
      restoreSkipped:
        "{{base}} {{count}} relation(s) skipped — source or target was not selected for restore.",
      purgeConfirm: "Permanently delete {{count}} item(s)? This cannot be undone.",
      purgedToast: "Deleted: {{concepts}} concept(s), {{relations}} relation(s).",
      purgeOlderConfirm: "Permanently delete all trash items dated before {{date}}?",
      purgedOlderToast:
        "Deleted: {{concepts}} concept(s), {{relations}} relation(s) older than {{date}}.",
      purgeAllConfirm:
        "Empty the trash entirely? All {{count}} items will be permanently deleted. This CANNOT be undone.",
      purgedAllToast: "Trash emptied: {{concepts}} concept(s), {{relations}} relation(s) deleted.",
    },
    audit: {
      introBefore: "",
      introBoldTitle: "Audit log",
      introAfter:
        " records who restored or permanently deleted what and when via the trash and snapshots. Use this trail to reconstruct why an object is (or is not) here.",
      introDetailBefore: "Read-only — perform actions via the ",
      introDetailSnapshots: "Snapshots",
      introDetailAnd: " and ",
      introDetailTrash: "Trash",
      introDetailAfter: " tabs.",
      filterLabel: "Filter",
      filterAll: "All actions",
      actionTrashRestore: "Trash: restored",
      actionTrashPurge: "Trash: permanently deleted",
      actionTrashPurgeShort: "Trash: deleted",
      actionSnapshotCreate: "Snapshot: created",
      actionSnapshotRestore: "Snapshot: restored",
      actionSnapshotDelete: "Snapshot: deleted",
      emptyNoFilter: "No audit entries found for this filter.",
      detailTrashRestore: "{{concepts}} concept(s), {{relations}} relation(s) restored.",
      detailTrashRestoreSkipped: "{{count}} relation(s) skipped (dangling endpoint).",
      detailTrashPurgeBefore: "Mode: ",
      detailTrashPurgeOlderThan: " older than {{date}}",
      detailTrashPurgeAfter:
        " — {{concepts}} concept(s), {{relations}} relation(s) permanently deleted.",
      detailSnapshotRestoreFrom: "Restored from ",
      detailSnapshotRestoreSafety: " — safety snapshot: ",
      detailSnapshotDeleteSuffix: " deleted",
    },
    maintenance: {
      introBefore: "",
      introBoldTitle: "Garbage collection",
      introAfter:
        " removes objects that point to non-existent concepts — orphan relations (source or target hard-deleted) and viewState entries left behind after a delete.",
      introDetailBefore: "Runs automatically ",
      introDetailBold: "on a schedule",
      introDetailAfter:
        " via Cloudflare Cron — by default daily at 03:00 UTC; a super admin sets the time under Settings → Workspace → Cron jobs. The button below runs it immediately for this workspace.",
      runNowButton: "Run now",
      runningButton: "Working…",
      lastRuns: "Recent runs",
      empty: "No runs performed for this workspace yet.",
      colWhen: "When",
      colTrigger: "Trigger",
      colStatus: "Status",
      colRelations: "Relations",
      colViewState: "ViewState",
      colDiagrams: "Diagrams",
      colWho: "By",
      triggerCron: "Cron",
      triggerManual: "Manual",
      statusSuccess: "Done",
      statusError: "Error",
      runDoneNothing: "Done in {{ms}}ms — nothing to clean up.",
      runDoneCleaned:
        "Done in {{ms}}ms — {{relations}} relations deleted, {{viewstates}} viewState entries cleaned.",
      retired: {
        heading: "Clean up retired objects",
        intro:
          "Auto-sync sources (e.g. MITRE CWE) mark objects that disappeared from their source catalog as «Retired» instead of removing them immediately. This action cleans them up for good.",
        scopeLabel: "Scope:",
        scopeWorkspace: "Workspace (all workpackages incl. Base)",
        scopeWorkpackage: "Workpackage:",
        scopeWorkpackageHint: "(Base is not an option here — use Workspace scope for Base.)",
        previewButton: "Show retired objects →",
        previewingButton: "Working…",
        previewEmpty: "No retired objects found in this scope.",
        resultHeading: "To be removed — confirm to proceed:",
        conceptsHeading: "Concepts ({{count}})",
        relationsHeading: "Relations ({{count}})",
        diagramsHeading: "Diagrams ({{count}})",
        cascadeHeading: "Cascade dependencies (also removed):",
        cascadeRelations:
          "{{count}} relations to retired concepts (not «Retired» themselves, but lose their endpoint)",
        cascadeViewState: "{{entries}} viewState entries on {{diagrams}} diagrams",
        cancelButton: "Cancel",
        confirmButton: "Delete permanently",
        confirmDialog:
          "This tombstones {{concepts}} concepts and {{relations}} relations, permanently deletes {{diagrams}} diagrams, and strips {{viewstate}} viewState entries. Continue?",
        cleaningButton: "Working…",
        cleanupDone:
          "Done — {{concepts}} concepts and {{relations}} relations tombstoned, {{diagrams}} diagrams deleted, {{viewstate}} viewState entries cleaned.",
      },
    },
    superAdmin: {
      navAria: "Installation admin sections",
      navGroupThis: "This workspace",
      navGroupAccess: "Access",
      navGroupInstall: "Installation",
      navThisWorkspace: "Name and access",
      thisWorkspaceIntro:
        "The name of the workspace you are working in, and an extra password for super admins who switch into it.",
      adminsIntro:
        "Everyone listed here manages the whole installation: workspaces, mail, AI and background jobs. At least one always remains.",
      workspacesIntro:
        "Create a workspace, empty or with the example model, or archive, delete and restore one.",
      globalMailIntro:
        "The fallback for every workspace without its own mail settings, and the sender of the sign-in codes.",
      statusSet: "Set",
      statusNotSet: "Not set",
      passwordSaved: "Password saved.",
      passwordCleared: "Password cleared.",
      you: "You",
      current: "Current",
      needsPassword: "Password",
      addAdminLabel: "Add a super admin",
      intro:
        "As a super admin you can temporarily (max 1 hour) switch into any workspace to help with setup or troubleshooting — you don't become a member, access expires automatically.",
      tabMasterPassword: "Master password",
      tabAdmins: "Super admins",
      tabSwitch: "Switch workspace",
      tabCronJobs: "Cron jobs",
      tabLegacySnapshots: "Old snapshots",
      tabWorkspaces: "Workspaces",
      workspacesNewHeading: "New workspace",
      workspacesName: "Name",
      workspacesLanguage: "Default language",
      workspacesAdmin: "Administrator (email)",
      workspacesCreate: "Create workspace",
      workspacesCreated: "Workspace '{{name}}' created; {{email}} is administrator.",
      workspacesOpen: "Open",
      languageEn: "English",
      languageNl: "Dutch",
      exampleHeading: "Example workspace",
      exampleIntro:
        "Creates a new workspace with the Meridiaan Bank model: a fictional bank with processes, capabilities, applications, a Business Model Canvas, C4 diagrams, risks and controls, a roadmap and a concept model. The user guide uses it throughout. You become its administrator.",
      exampleName: "Meridiaan Bank (example)",
      exampleButton: "Create example workspace",
      exampleBusy: "Creating the example workspace…",
      archiveHeading: "Archive, delete and restore",
      archiveIntro:
        "An archive is a complete copy of a workspace: workpackages, plateaus, folders, objects, diagrams, teams, members and permissions. Not included: snapshots, API tokens and the workspace's mail password. You can restore an archive into this or another installation, as long as the workspace no longer exists there.",
      archiveDownload: "Download archive",
      archiveSaved: "Archive of '{{name}}' saved as {{file}} ({{rows}} rows).",
      deleteButton: "Delete…",
      deleteHeading: "Delete workspace '{{name}}'",
      deleteWarning:
        "This permanently deletes the workspace with all workpackages, branches, snapshots and members. Only the archive you download first can bring it back.",
      deleteArchived: "Archive downloaded",
      deleteConfirm: "Type '{{name}}' to confirm",
      deleteFinal: "Delete workspace",
      deleteDone: "Workspace '{{name}}' deleted.",
      cancel: "Cancel",
      restoreHeading: "Restore archive",
      restoreIntro:
        "Choose an archive file. It comes back as the same workspace, with the same identifiers, so links and AI connections keep working. Not possible while that workspace still exists; for a copy, use Export / Import per workpackage.",
      restoreSummary:
        "Workspace '{{name}}', archived {{date}}: {{rows}} rows in {{tables}} tables.",
      restoreButton: "Restore",
      restoreDone:
        "Workspace '{{name}}' restored. Re-enter its mail password if it had its own mail settings.",
      restoreMismatch: "Workspace '{{name}}' restored, but counts differ: {{tables}}.",
      progress_download: "Downloading",
      progress_rows: "Restoring rows",
      progress_links: "Restoring links",
      progress_finish: "Checking",
      exampleIncomplete:
        "The example workspace was created, but {{count}} items could not be imported. See the Worker log.",
      exampleCreated:
        "Workspace '{{name}}' created with {{concepts}} objects, {{relations}} relations and {{diagrams}} diagrams.",
      legacySnapshots: {
        colDate: "Created",
        colScope: "Scope",
        colLabel: "Label",
        intro:
          "Snapshots from before snapshot points (format 1) cannot be restored safely when they cover a branch. Clean them up here across all workspaces, together with their files. Files under snapshots/ without a snapshot go too; nothing else in the bucket is touched.",
        summary: "{{snapshots}} old snapshot(s) and {{files}} orphaned file(s).",
        empty: "There are no old snapshots or orphaned files left.",
        purge: "Delete everything above",
        confirm:
          "Permanently delete {{snapshots}} old snapshot(s) and {{files}} orphaned file(s) across all workspaces? This cannot be undone.",
        done: "{{snapshots}} snapshot(s) and {{files}} file(s) deleted.",
      },
      cronJobs: {
        disabledPill: "Disabled",
        running: "Running",
        triggerCron: "Scheduled",
        triggerManual: "Manual",
        statusSuccess: "Succeeded",
        statusError: "Failed",
        intro:
          "Cloudflare's cron trigger fires every hour; these settings decide whether a job actually runs on that tick. Times are shown in Amsterdam time and stored as UTC under the hood.",
        jobMitre: "MITRE CWE sync",
        jobGc: "Orphan GC",
        hourLabel: "Hour",
        tzLabel: "Amsterdam",
        hourUtcTooltip: "{{hourUtc}}:00 UTC",
        everyLabel: "Every",
        daysLabel: "days",
        runNowButton: "Run now",
        showHistory: "Show history",
        hideHistory: "Hide history",
        lastRun: "Last run: {{when}}",
        neverRun: "Never run yet.",
        noRuns: "No runs yet.",
        colSummary: "Summary",
        dispatchedInfo:
          "Started — the MITRE CWE sync now runs via GitHub Actions (fetch/parse) and reports back when done. Check the history for progress.",
        possiblyStuck: "Possibly stuck",
        possiblyStuckTooltip:
          "This run has been 'running' for over 30 minutes without finishing — the GitHub Actions run may have crashed before it could report back.",
      },
      passwordHint:
        "At least 15 characters, with an uppercase letter, lowercase letter, digit, and special character.",
      currentPasswordLabel: "Current password",
      newPasswordLabel: "New password",
      setButton: "Set",
      changeButton: "Change",
      clearButton: "Clear",
      workspaceNameTitle: "Name of this workspace",
      workspaceNameHint:
        "Shown in the header, in the workspace switcher and in every export filename. Exports already downloaded and existing snapshot labels keep the old name and do not change with it.",
      workspaceNameSaved: "Name saved.",
      workspacePasswordFor: "Access password for '{{name}}'",
      workspacePasswordHint:
        "Extra gate a super admin must still pass after the master password, before switching into THIS workspace.",
      masterPasswordHint:
        "Protects the workspace list under 'Switch workspace'. Without a master password, every super admin sees that list straight away.",
      adminsHeading: "Super-admin e-mail addresses",
      addEmailPlaceholder: "name@example.com",
      addButton: "Add",
      removeButton: "Remove",
      removeLastError: "Cannot remove the last super admin.",
      switchMasterPasswordLabel: "Master password",
      switchMasterPasswordPlaceholder: "Enter the master password",
      switchRevealButton: "Show workspaces",
      switchWorkspaceAccessLabel: "Access password of this workspace",
      switchButton: "Switch",
      switchingButton: "Working…",
      switchDone: "Switched to '{{name}}'.",
      incorrectPassword: "Password is incorrect.",
      overrideBanner: "⚡ Super admin: {{name}}",
      overrideBackButton: "↩ Back to my workspaces",
    },
  },
  textPane: {
    title: "Text pane",
    openButton: "▲ Text pane",
    openHint: "Open text pane",
    hideButton: "▼ Hide",
    hideHint: "Hide text pane",
    variantSelectorLabel: "Text pane:",
    variantTextLegacy: "Text",
    variantGrid: "Table",
    parseErrors_one: "{{count}} parse error",
    parseErrors_other: "{{count}} parse errors",
    errorLine: "line {{line}}: {{message}}",
    preview: {
      hideTitle: "Hide live preview",
      showTitle: "Show live preview (Mermaid client-side, PlantUML via Kroki)",
      on: "👁 Preview on",
      off: "👁 Preview",
      header: "Live preview ({{notation}})",
      rendering: "rendering…",
      resetTitle: "100% (natural size)",
      fitTitle: "Fit to pane",
      empty: "No preview yet — type something in the editor or activate a diagram.",
      unexpectedContentType: "Unexpected content type: {{contentType}}",
      unknownError: "Unknown error during preview render.",
    },
    perInstance: {
      onTitle:
        "Per-instance mode on — one line per visual instance. Not ArchiMate-compliant; Kroki rendering may be less accurate.",
      offTitle:
        "Per-instance mode off — one line per concept id (logical view, ArchiMate-compliant).",
      on: "🧬 Per-instance on",
      off: "🧬 Per-instance",
    },
    search: {
      title: "Search in text (Cmd/Ctrl+F)",
      aria: "Search in text",
    },
    pin: {
      pinnedTitle: "Pinned — pane stays open after refresh. Click to unpin.",
      unpinnedTitle: "Pin — pane stays open after refresh.",
    },
    lineType: {
      label: "Line:",
      title: "PlantUML line routing — switch to see which layout works best for this diagram",
      curved: "Curved",
      polyline: "Polyline",
      ortho: "Orthogonal",
    },
    copy: {
      title: "Copy text to clipboard",
      label: "Copy",
      copied: "✓ Copied",
      labelLower: "copy",
      copiedLower: "✓ copied",
    },
  },
  gridPane: {
    statusBar:
      "Table (new) — {{rows}} rows — rev #{{revision}} — double-click column B to edit name/label — Arrow ↑↓ / Shift+Arrow for keyboard selection",
    searchPlaceholder: "Search the table…",
    searchAriaLabel: "Search the table",
    noActiveDiagram: "No active diagram or workspace",
    parseErrors_one: "{{count}} parse error: {{errors}}",
    parseErrors_other: "{{count}} parse errors: {{errors}}",
    selectedCount: "{{n}} selected",
    selectionKeyHint:
      "Shift-click = range, Cmd/Ctrl-click = toggle, DEL = delete, right-click = menu",
    columns: {
      b: "B — Textual",
      c: "C — Pos / Source",
      d: "D — Target handle",
      e: "E — Stereotypes",
      f: "F — Anchor",
    },
    titles: {
      editName: "Double-click to edit the name",
      editLabel: "Double-click to edit the label",
      editPosition: "Double-click to edit the position (x, y)",
      pickSourceHandle: "Double-click to choose the source handle",
      pickTargetHandle: "Double-click to choose the target handle",
      assignStereotypes: "Double-click to assign stereotypes",
      editStereotypes: "Double-click to edit stereotypes — {{n}} active",
    },
    noRows: "No rows — select a diagram or add an object first.",
    menu: {
      pasteHere: "Paste here ({{n}})",
      cancelMove: "Cancel move",
      insertRow: "Insert row (Alt+N)",
      showOnCanvas: "Show on canvas",
      move: "Move",
      delete: "Delete ({{n}})",
      deselect: "Deselect",
      removeFromDiagram: "Remove from diagram ({{n}})",
      deleteFromModel: "Delete from model… ({{n}})",
    },
    stereotypeModal: {
      titlePrefix: "Stereotypes for",
      noneAvailable: "No stereotypes available for this type.",
      clear: "Clear",
      cancel: "Cancel",
      saving: "Saving…",
      save: "Save",
    },
  },
  parentDelete: {
    title: "Objects deleted on the parent layer",
    description:
      "These objects were deleted on the parent layer, but this branch still uses them in its own relations. Choose per object what should happen.",
    usedIn: "Used in {{count}} branch relation(s)",
    keep: "Keep as branch object",
    accept: "Delete",
    later: "Later",
  },
  homonymLink: {
    title: "Possibly the same object",
    description:
      "These objects on this branch share a name and type with an object on the parent layer but are not linked. Link them to treat them as one object (the branch version is fully preserved), or dismiss if they are genuinely two different things.",
    matchHint: "Same name and type as an object on the parent layer.",
    link: "Link",
    dismiss: "Dismiss",
    later: "Later",
  },
  plateauDiff: {
    title: "Plateau — diagrams vs the preceding plateau",
    comparedTo: "vs {{plateau}}",
    statusAdded: "New",
    statusChanged: "Changed",
    statusDeleted: "Deleted",
    statusNeutral: "Unchanged",
    cardHint: "Click = select · double-click = open",
    detailTitle: "Changes on this diagram",
    elementChanges: "{{count}} change(s)",
    noElementChanges: "No element changes on this diagram.",
    deletedDetail: "This diagram was deleted vs the previous plateau.",
    loading: "Loading diagrams…",
    error: "Could not load the diagram diff.",
    empty: "No diagrams on this plateau.",
  },
  sharp: {
    levelArea: "process area",
    levelBusiness: "business process",
    levelSub: "subprocess",
    nestLevelInverted:
      "Careful: you are nesting a {{child}} inside a {{parent}}. That inverts the levels — Sharp goes from process area to business process to subprocess. The aggregation was created; reverse it if this was not intended.",
  },
  swimlane: {
    resultSymbol: "Process result",
    legendFlow: "Flow — dashed, with arrow",
    legendTriggering: "Triggering — solid, with arrow",
    stepDelays: "This step holds the work up",
    laneHeightHint: "Drag to make the lane taller or shorter",
    delay: "This handoff takes time — {{value}}",
    titleHint:
      "This diagram’s name — click for its properties (such as the point in time), double-click to rename",
    newLaneZoneTop: "Drop a performer here for a lane at the top",
    newLaneZone: "Drop a performer here for a new lane, or a step to unassign it",
    arrange: "Arrange the steps",
    arrangeHint: "Put every step in its performer’s lane, at the place the flow gives it",
    connectHint: "Drag to another step to draw a flow between them",
    resizeHint: "Drag to make the card larger or smaller",
    noFlowBetween: "These two cannot be connected by a flow on this diagram.",
    flowRule: {
      mergeQuestion: "This step already has an incoming flow. How do the two come together?",
      splitQuestion: "This step already has an outgoing flow. How does the work split?",
      xor: "XOR — exclusive",
      and: "AND — parallel",
      of: "OR — one or more",
      samen: {
        xor: "The work arrives along exactly one of the routes",
        and: "Wait until the work has arrived along all routes",
        of: "The work arrives along one or more routes",
      },
      splits: {
        xor: "The work continues along exactly one of the routes",
        and: "The work continues along all routes at once",
        of: "The work continues along one or more routes",
      },
      cancel: "Cancel",
      both: "This flow would break two rules at once: the source step already has an exit and the target step already has an entry. Place a branch or merge yourself first.",
      violation:
        "This step has more than one incoming or outgoing flow. Merging and splitting belong in an XOR, AND or OR, so the meaning is on the diagram. Only a decision step may have several outgoing branches.",
      shouldDecide:
        "An XOR follows this step, so this step chooses which branch is taken. Give it the Decision Step stereotype in the attribute panel.",
    },
    branch: {
      xor: "Branch: exactly one path",
      and: "Branch: all paths",
      of: "Branch: one or more paths",
      samen: "Merge point: the routes come back together here",
    },
    laneOwnerHint:
      "Double-click to edit the performer. Drop another performer on it to hand the lane over — its steps move along.",
    unassigned: "Not yet assigned",
    unassignedHint: "Drag a step into a lane to record who performs it",
    empty: "No steps on this diagram yet. Drag a step from the palette.",
    noLanes:
      "No lanes yet. Drag a performer from the palette — a role, system, device or holding area.",
    handoffs_one: "{{count}} handoff",
    handoffs_other: "{{count}} handoffs",
    steps_one: "{{count}} step",
    steps_other: "{{count}} steps",
    lanePool: "Under {{name}}",
    legendHandoff: "Handoff — the work changes lane",
    legendSequence: "Sequence — within the same lane",
    unnamed: "(unnamed)",
    svgTitle: {
      handoff: "handoff",
      sequence: "triggering",
      flows: "Flows between steps",
    },
  },
  glossary: {
    title: "Attribute types",
    count: "{{count}} attribute type(s)",
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    colName: "Name",
    colDefinition: "Definition",
    empty: "No attribute types yet. Click Add.",
    unnamed: "(unnamed)",
    readOnly: "Read-only — this workpackage has no write access to this folder",
    addTitle: "New attribute type",
    editTitle: "Edit attribute type",
    name: "Name",
    nameNl: "Name (NL)",
    nameEn: "Name (EN)",
    defNl: "Definition (NL)",
    defEn: "Definition (EN)",
    cancel: "Cancel",
    save: "Save",
    confirmDelete: "Delete attribute type '{{name}}'?",
  },
  roadmap: {
    aiBadge: "AI",
    aiBadgeTitle:
      "Created by an AI through MCP. Here the AI may restore its own snapshots and discard the plateau. Ctrl+Z does not undo what the AI did; use a snapshot for that.",
    title: "Roadmap — plateaus over time",
    subtitle:
      "Each lane is a plateau (version of the world). Drag the time slider to make a plateau active.",
    now: "now",
    active: "active",
    currentSituation: "Current situation",
    switchHint: "Click = time-travel to this plateau",
    mergeIntoParent: "⇥ Merge into {{parent}}",
    mergeHint: "Let time move on: merge this plateau into the previous one",
    laneHint: "Placing I&M elements is coming",
    dropHint: "Drag a Work Package here",
    dropWpOnLane: "Drop a Work Package onto a plateau. Drop a Deliverable onto a Work Package.",
    dropDeliverableOnWp: "Only a Deliverable can be dropped onto a Work Package.",
    moveOrCopyDeliverable:
      "This deliverable already belongs to another work package. Move it (old link removed) or also link it here (copy)?",
    move: "Move",
    copy: "Copy",
    cancel: "Cancel",
    ok: "OK",
    removeFromRoadmap: "Remove",
    hardDelete: "Delete permanently",
    plateau1NoConcept: "This plateau has no plateau object yet — place on a future plateau.",
    onlyWpDeliverable: "Only Work Packages and Deliverables can be placed in a plateau.",
    unnamed: "(unnamed)",
    workPackage: "Work Package — realizes this plateau",
    deliverable: "Deliverable — arises at this plateau",
    gaps: "Gaps",
    differences: "Differences",
    addPlateau: "Add plateau",
    addConfirm: "Add",
    plateauNamePlaceholder: "Plateau name…",
    timeSlider: "Time",
    loading: "Loading roadmap…",
    error: "Could not load the roadmap.",
  },
  roadmapMatrix: {
    title: "Roadmap matrix",
    subtitle:
      "Evolution of chosen elements across the plateaus. Drag only the things of interest onto it.",
    now: "Now",
    elementColumn: "Element",
    notConfiguredTitle: "Not configured yet",
    notConfiguredHint:
      "Pick a Program (= Roadmap) and a concept type in the Diagrams panel. The columns then come from that Program's plateaus.",
    loading: "Loading plateaus…",
    error: "Could not load the plateaus.",
    dropHint: "Drag elements onto the matrix here",
    unnamed: "(unnamed)",
    configTitle: "Roadmap matrix configuration",
    programLabel: "Program (Roadmap)",
    programChoose: "— choose Program —",
    programNone: "No Program in this workspace. Create one first (Roadmap diagram).",
    domainLabel: "Concept type",
    domainChoose: "— choose type —",
    setupTitle: "Create roadmap matrix",
    setupConfirm: "Create",
    active: "active",
    dropElementHint: "Drag an element here",
    dropTypeMismatch: "This element does not match the matrix's chosen concept type.",
    statePlaceholder: "state…",
    notInPlateau: "This object does not exist in this plateau",
    translating: "translating…",
    removeRow: "Remove row from the matrix",
  },
  zoomPan: {
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    reset: "Reset zoom to 100%",
  },
  boxFormat: {
    font: "Font",
    size: "Font size",
    fontColor: "Text colour",
    fillColor: "Fill colour",
    align: {
      left: "Align left",
      center: "Centre",
      right: "Align right",
    },
    vAlign: {
      top: "Top",
      middle: "Middle",
      bottom: "Bottom",
    },
    clear: "Clear formatting",
    close: "Close",
  },
  gapDiff: {
    title: "Gap — changes vs the preceding plateau",
    tabObjects: "Objects",
    tabRelations: "Relations",
    tabReifications: "Reifications",
    viewModel: "Model",
    viewTable: "Table",
    colField: "Field",
    colBefore: "Before",
    colAfter: "After",
    currentSituation: "Current situation",
    fields: {
      typeId: "Type",
      name: "Name",
      description: "Description",
      stereotypes: "Stereotypes",
      source: "Source",
      target: "Target",
      label: "Label",
    },
    valueYes: "Yes",
    valueNo: "No",
    statusAdded: "Added",
    statusChanged: "Changed",
    statusDeleted: "Deleted",
    empty: "No changes vs the preceding plateau.",
    loading: "Loading diff…",
    error: "Could not load the diff.",
    reificationsHint: "Reifications follow once the reification primitive is built.",
    onMain: "This is the main version — no predecessor to compare against.",
  },
  typeExplorer: {
    title: "Type explorer",
    launch: "Type explorer",
    launchDesc: "Overview of all types and concepts in the active branch.",
    close: "Close",
    empty: "No concepts for these filters.",
    count: "{{count}} concepts",
    search: "Search by name…",
    filters: "Filters",
    clearFilters: "Clear",
    noStereotypes: "(no stereotypes)",
    jumpHint: "Click to jump to a diagram",
    notOnDiagram: "Not on any diagram",
    col: {
      name: "Name",
      type: "Type",
      stereotype: "Stereotype",
      layer: "Layer",
      diagrams: "# diagrams",
      lastEdit: "Last edited",
    },
  },
  subStereotype: {
    title: "What kind of {{base}}?",
    hint: "Pick a more specific type, or leave it generic.",
    generic: "{{base}} (generic)",
    cancel: "Cancel",
    confirm: "Choose",
  },
  merge: {
    title: "Merge '{{source}}' into '{{target}}'",
    titleLoading: "Merge '{{source}}'",
    loading: "Calculating the dry run…",
    intro:
      "The content of '{{source}}' is merged into '{{target}}'. '{{source}}' is kept as history and can no longer be edited.",
    added: "Added",
    changed: "Changed",
    deleted: "Removed",
    kind: {
      concept: "Objects",
      relation: "Relations",
      diagram: "Diagrams",
    },
    children: "Get '{{target}}' as parent: {{list}}.",
    field: {
      name_json: "Name",
      description_json: "Description",
      label_json: "Label",
      attributes_json: "Attribute {{key}}",
      stereotypes_json: "Stereotype {{key}}",
      tags_json: "Tag {{key}}",
      alt_labels_json: "Alternative names",
      type_id: "Type",
      parent_id: "Parent object",
      source_concept_id: "Source",
      target_concept_id: "Target",
      name: "Name",
      notation_pack: "Notation",
      view_kind: "Diagram kind",
    },
    conflictsTitle: "Conflicts",
    noConflicts: "No conflicts.",
    conflict: {
      deletedOnTarget: "'{{source}}' changed this, '{{target}}' deleted it.",
      deletedOnTarget_source: "Bring it back with the branch version",
      deletedOnTarget_target: "Leave it deleted",
      changedOnTarget: "'{{source}}' deleted this, '{{target}}' changed it afterwards.",
      changedOnTarget_source: "Delete",
      changedOnTarget_target: "Keep",
    },
    propagationTitle: "Other branches",
    propagation: {
      updated: "{{branch}}: '{{name}}' takes over {{taken}} field(s) and keeps {{kept}} of its own",
      rebased: "{{branch}}: '{{name}}' keeps its own values",
      deletedOnParent:
        "{{branch}}: '{{name}}' is removed but still has its own copy there; the branch gets the question whether it is still in use",
      noBase: "{{branch}}: '{{name}}' is too old to update; please check it yourself",
    },
    duplicates_one: "One object was on the branch twice; the newest version is merged.",
    duplicates_other: "{{count}} objects were on the branch twice; the newest version is merged.",
    safetyNote: "A restore point is made first. Restoring it (Snapshots) undoes the merge.",
    cancel: "Cancel",
    confirm: "Merge",
    running: "Merging…",
    cancelled: "Merge cancelled.",
    operationLabel: "Merge '{{source}}' into '{{target}}'",
    done: "'{{source}}' has been merged into '{{target}}'. The restore point is under snapshots.",
  },
  operations: {
    kind: {
      import: "Import",
      restore: "Restore",
      merge: "Merge",
    },
    preview: {
      title: {
        import: "What this import will do",
        restore: "What this restore will do",
        merge: "What this merge will do",
      },
      removed_one: "1 object disappears",
      removed_other: "{{count}} objects disappear",
      overwritten_one: "1 existing object is overwritten",
      overwritten_other: "{{count}} existing objects are overwritten",
      diagrams_one: "1 diagram is affected",
      diagrams_other: "{{count}} diagrams are affected",
      diagramAction: {
        update: "changed",
        insert: "new",
        remove: "disappears",
      },
      created_one: "1 object is added",
      created_other: "{{count}} objects are added",
      reused_one: "1 object is left alone",
      reused_other: "{{count}} objects are left alone",
      nothingOverwritten: "Nothing existing is overwritten.",
      noneListed: "No names to show.",
      truncated: "Not every name is listed — the counts are complete.",
      confirm: "Continue",
      cancel: "Cancel",
      close: "Close",
      show: "What changes?",
    },
    countdown: "{{kind}} by {{who}} starts in {{time}}",
    quietNow: "{{kind}} starts in {{time}} — wrap up your work",
    dueNow: "{{kind}} is ready to start — {{who}} kicks it off",
    running: "{{kind}} is running — hold off on editing",
    impactOverwritten_one: "overwrites 1 existing object",
    impactOverwritten_other: "overwrites {{count}} existing objects",
    impactRemoved_one: "1 object disappears",
    impactRemoved_other: "{{count}} objects disappear",
    keepWorking: "You can keep working and saving.",
    awaiting: "Waiting for: {{who}}",
    askedForTime: "More time requested by: {{who}}",
    ready: "I'm done, go ahead",
    readyDone: "You're done",
    needMoreTime: "I need more time",
    startNow: "Start now",
    cancel: "Call it off",
  },
  locks: {
    heldBy: "{{holder}} is editing this object — you cannot change it right now.",
    heldByOccurrence: "{{holder}} is editing this occurrence — you cannot change it right now.",
    heldByBatch: "An import or restore is running here — editing resumes shortly.",
    lost: "Your hold on this object lapsed — check your change before you continue.",
  },
  chat: {
    attachDiagram: "Refer to {{name}}",
    hint: "Enter sends. Ctrl+Enter, Alt+Enter or Shift+Enter starts a new line. Esc closes. Messages disappear when the app closes.",
    close: "Close",
    title: "Message to {{email}}",
    unauthorized:
      "This diagram is not available here — you may not have access, or it lives on another branch.",
    empty: "No messages yet. What you send here is not kept.",
    unread: "Unread message",
    placeholder: "Type a message…",
    send: "Send",
  },
  presence: {
    workingIn: "Working in: {{name}}",
    onlyYou: "Only you are online",
    youPlusOthers_one: "You + {{count}} other online",
    youPlusOthers_other: "You + {{count}} others online",
    meLabel: "{{email}} (you)",
  },
  apiTokens: {
    hideRevoked: "Hide revoked tokens",
    showRevoked: "Show revoked tokens ({{count}})",
    newTitle: "New token",
    intro:
      "An API token lets a program work on your behalf: the MCP server Claude uses to model, the desktop or iPad app, or a script of your own. A token never has more rights than you do.",
    scopeLabel: "Level",
    scopes: {
      read: "Read",
      model: "Model",
      admin: "Admin",
    },
    scopeHelp: {
      read: "View only.",
      model: "View and model: objects, relations, diagrams, branches.",
      admin: "Also administration: workpackages, teams, permissions, folders, import and restore.",
    },
    name: "Name",
    namePlaceholder: "e.g. Claude on my laptop",
    workspace: "Workspace",
    allWorkspaces: "All workspaces I am a member of",
    expiry: "Valid for",
    expiryDays: "{{count}} days",
    expiryNever: "No expiry",
    create: "Create token",
    creating: "Working…",
    createdTitle: "Copy your token now",
    createdBody: "This is the only time you will see it. Keep it like a password.",
    copy: "Copy",
    copied: "Copied",
    done: "Done",
    listTitle: "Your tokens",
    empty: "You have no tokens yet.",
    colName: "Name",
    colScope: "Level",
    colClient: "Via",
    colLastUsed: "Last used",
    colExpires: "Expires",
    never: "never",
    revoke: "Revoke",
    revoked: "revoked",
    revokeConfirm: "Revoke token “{{name}}”? Anything using it stops working immediately.",
    colAi: "AI writes",
    aiPlateauOnly: "AI plateaus only",
    aiAnywhere: "where the workpackage allows",
    aiToggleTitle:
      "What this AI connection may write. The workpackage setting always applies; this can only restrict further.",
    mcpTitle: "Connecting Claude via MCP",
    mcpBody:
      "No need to copy a token: start the MCP server without one, it opens this app with a pairing code and you click Allow.",
  },
  connect: {
    title: "Approve connection",
    loading: "Loading request…",
    codeLabel: "Pairing code",
    codeHelp: "Check that this matches the code the app shows.",
    enterCode: "Enter the pairing code shown by the app",
    lookup: "Look up",
    asks: "{{client}} wants to work on your behalf ({{email}}).",
    scopeLabel: "With level",
    workspaceLabel: "Restricted to workspace",
    anyWorkspace: "All workspaces you are a member of",
    aiPlateauOnly: "Only write on own AI plateaus",
    aiPlateauOnlyHelp:
      "The AI then works on its own plateau on the roadmap and never directly on main or your branches, even where the workpackage allows more.",
    approve: "Allow",
    deny: "Deny",
    approvedTitle: "Connected",
    approvedBody: "You can close this window; the app continues now.",
    deniedTitle: "Denied",
    deniedBody: "The app gets no access.",
    backToEditor: "Go to the editor",
    manageTokens: "Manage tokens under Settings → API tokens.",
  },
  aiWrite: {
    label: "What may AI write here",
    none: "Nothing — AI may only read",
    ai_plateau: "Only on its own AI plateau",
    anywhere: "Anywhere the user may write",
    help: {
      none: "An AI (via the MCP connection in Claude) can only read in this workpackage; it cannot even create an AI plateau.",
      ai_plateau:
        "An AI creates its own plateau on the roadmap and writes only there. Main, your branches, folders and workpackage-wide settings stay closed. You see what the AI did at a glance, and taking it over is a deliberate merge.",
      anywhere:
        "An AI may write wherever the user may write, including main. Note: Ctrl+Z in the browser does not undo AI changes.",
    },
    note: "Applies only to AI connections (MCP), not to the browser or the admin apps. A connection can be set stricter itself; the strictest applies.",
    saved: "AI setting saved.",
  },
  wpSettings: {
    menu: "Workpackage settings…",
    menuDesc: "Name, description and what an AI may do in this workpackage.",
    title: "Workpackage settings",
    nameLabel: "Name",
    descriptionLabel: "Description",
    save: "Save",
    close: "Close",
    saved: "Saved.",
    readOnly:
      "You can view these settings; a workspace admin or a team with write access to this workpackage can change them.",
    baseReadOnly:
      "The Base workpackage has a fixed name and description; only the AI setting can be changed.",
    noWorkpackage: "Choose a workpackage first.",
    loading: "Loading…",
  },
  importing: {
    readingFile: "One moment please, I am reading the file…",
    updatingTranslations: "One moment please, I am updating translations…",
    mergingDuplicates: "One moment please, I am merging duplicate objects…",
    linkingContainers: "One moment please, I am linking objects to their container…",
  },
  scopePicker: {
    title: "Choose focus scope",
    hint: "↑↓ navigate, Enter confirm, 1-8 direct.",
    current: "current",
    scopes: {
      canvas: "Canvas",
      explorer: "Explorer",
      diagramsPanel: "Diagrams panel",
      typePalette: "Type palette",
      relationsPanel: "Relations panel",
      textPane: "Text pane",
      attributePanel: "Attribute panel",
      toolbar: "Canvas toolbar",
    },
    short: {
      canvas: "Canvas",
      explorer: "Explorer",
      diagramsPanel: "Diagrams",
      typePalette: "Type palette",
      relationsPanel: "Relations",
      textPane: "Text pane",
      attributePanel: "Attributes",
      toolbar: "Toolbar",
    },
  },
  help: {
    title: "❓ User guide",
    versionNote:
      "Guide version: baked in at build time. When the documentation changes, the new version appears after a redeploy.",
    appVersion: "Arch-designer {{version}}",
    serverVersion: "server {{version}}",
  },
  auth: {
    gate: {
      authenticating: "Authenticating…",
      apiFailed: "Connection to the API failed",
      unexpectedStatus: "API returned unexpected status {{status}}",
      networkError: "network error",
    },
    login: {
      lead: "Model together in one repository: ArchiMate 4, C4, Sharp and more. Every diagram is a view on the same model.",
      emailHeading: "Sign in",
      emailHint: "We will send a six-digit code to this address.",
      codeHeading: "Enter your code",
      codeDelay: "The e-mail can take a minute or two to arrive.",
      codeResent: "New code sent.",
      language: "Language",
      version: "Version {{version}}",
      emailLabel: "E-mail address",
      sendCode: "Send sign-in code",
      busy: "Working…",
      wait: "Wait {{time}}",
      codeSentTo: "Code sent to",
      otherAddress: "Different address?",
      codeLabel: "6-digit code",
      signIn: "Sign in",
      resendIn: "Send again ({{seconds}}s)",
      resend: "Send the code again",
      tooManyAttempts: "Too many attempts. Try again in {{time}}.",
      requestFailed: "Could not request a code. Please try again.",
      codeInvalid: "Code is invalid or expired. Request a new code.",
      networkError: "Network error — please try again.",
      mailNotConfigured:
        "This installation cannot send login codes yet. A super admin first sets up mail under Settings → Workspace → E-mail (global).",
    },
    sessionExpiring: {
      messageBefore: "⚠ Your session expires in ",
      messageAfter: ". Renew now so you don't get signed out in the middle of your working day.",
      oneDay: "1 day",
      lessThanADay: "less than a day",
      days: "{{n}} days",
      renew: "Renew",
      notNow: "Not now",
    },
  },
  repositoryMenu: {
    groupView: "View",
    groupSnapshots: "Snapshots",
    groupCleanup: "Clean up",
    groupCleanupNote: "Takes a snapshot first.",
    groupExchange: "Import and export",
    button: "Repository",
    glossaryTitle: "Attribute types (glossary)",
    glossaryDesc: "The shared attribute types in a table.",
    typeExplorerTitle: "Type explorer",
    typeExplorerDesc: "All types and concepts in this branch.",
    snapshotMakeTitle: "Create snapshot",
    snapshotMakeDesc: "Saves the current state: the workpackage, branch or folder.",
    snapshotMade:
      "Snapshot created: {{concepts}} concepts, {{relations}} relations, {{diagrams}} diagrams, {{viewStates}} viewState entries.",
    snapshotReused: "Nothing has changed since snapshot '{{label}}' — no new one created.",
    restoreStructureIntro: "This snapshot also restores the branches below it to how they were:",
    restoreLater: "Cannot be restored right now.",
    restoreNever: "Can no longer be restored.",
    snapshotCleanup: "Clean up",
    snapshotCleanupConfirm:
      "Snapshot '{{label}}' can no longer be restored. Delete it permanently?",
    restoreBranchRemoved: "• '{{name}}' is removed (did not exist yet)",
    restoreBranchRecreated: "• '{{name}}' comes back",
    snapshotRestoreTitle: "Restore snapshot…",
    snapshotRestoreDesc: "Pick an earlier restore point of this workpackage, branch or folder.",
    snapshotRestoreModalTitle: "Restore snapshot",
    snapshotRestoreModalEmpty: "No snapshots found for this scope.",
    snapshotRestoreModalConfirm: "Restore",
    snapshotRestoreModalCancel: "Cancel",
    snapshotRestored:
      "Snapshot restored: {{concepts}} concepts, {{relations}} relations, {{diagrams}} diagrams, {{viewStates}} viewState entries.",
    snapshotRestoreConfirm:
      "Snapshot '{{label}}' will be restored within the current scope. A safety snapshot is made first. Continue?",
    dedupeTitle: "Merge duplicate objects",
    dedupeDesc: "Merges concepts with the same name, type and stereotypes.",
    dedupeConfirm:
      "All concepts with the same name + type + stereotype set will be merged. A safety snapshot is made first. Continue?",
    dedupeDoneNone: "No duplicate concepts found.",
    dedupeDone: "Done — {{groups}} group(s) merged, {{losers}} duplicates removed.",
    autoNestTitle: "Detect nesting (all diagrams)",
    autoNestDesc:
      "A concept that lies inside a larger frame gets that frame as its parent. For one diagram: Cmd+Shift+F.",
    autoNestConfirm:
      "For every concept without a parent that falls geometrically inside a larger container — across all diagrams in this workpackage — the container is set as its parent. A safety snapshot is made first. Continue?",
    autoNestDoneNone: "No missing nesting relations found.",
    autoNestDone: "Done — {{count}} concept(s) linked across {{diagrams}} diagram(s).",
    linkAttrTitle: "Link attributes to their concept",
    linkAttrDesc:
      "A Sharp attribute or identifier nested in a Concept gets that Concept as its owner. One-off, for existing data.",
    linkAttrConfirm:
      "For every Sharp attribute/identifier without an owner that is nested inside a Concept on a diagram, that Concept is set as its parent_id. A safety snapshot is made first. Continue?",
    linkAttrDoneNone: "All attributes/identifiers were already linked.",
    linkAttrDone: "Done — {{count}} attribute/identifier(s) linked to their concept.",
    translateTitle: "Fill in translations",
    translateDesc: "Fills the empty NL or EN side of bilingual fields with a translation.",
    translateConfirm:
      "For all concepts on this branch: where one language field (NL or EN) is empty and the other is filled, the translation will be filled in automatically. A safety snapshot is made first. Continue?",
    translateDone:
      "Done — {{filled}} field(s) filled across {{concepts}} concept(s) and {{diagrams}} diagram(s).",
    ameffTitle: "Import AMEFF…",
    ameffDesc:
      "Adds the objects from an ArchiMate XML file to this branch. Takes a snapshot first.",
    ameffPrefix: "AMEFF import",
    jsonTitle: "Import JSON…",
    jsonDesc: "Loads a JSON export into this branch. Takes a snapshot first.",
    jsonPrefix: "JSON import",
    importCancelled: "Import called off — nothing changed.",
    restoreCancelled: "Restore called off — nothing changed.",
    exportAmeffTitle: "Export as AMEFF (.xml)",
    exportAmeffDesc: "The branch you are on, as an ArchiMate Open Exchange file.",
    exportJsonTitle: "Export as JSON",
    exportJsonDesc: "The branch you are on, as a JSON file for this app.",
    exportDone: "'{{filename}}' downloaded ({{size}}).",
    exportSkipped: "{{count}} concept(s) omitted — AMEFF has no equivalent for: {{types}}.",
    exportFailed: "Export failed: {{msg}}",
    zeroItems: "0 items",
    unitConcepts: "concepts",
    unitRelations: "relations",
    unitDiagrams: "diagrams",
    unitViewStates: "viewState entries",
    unitFoldersCreated: "folders created",
    unitSkippedConcepts: "concept(s) skipped",
    unitSkippedRelations: "relation(s) skipped",
    unitAttentionRelations: "relation(s) to check",
    unitSkippedDiagrams: "diagram(s) skipped",
    unitUpdatedConcepts: "concepts updated",
    unitUpdatedRelations: "relations updated",
    unitUpdatedDiagrams: "diagrams updated",
    unitReusedConcepts: "concepts reused",
    unitSuffix: "marked [imported]",
    unknownElements: "unknown element types",
    unknownRelationships: "unknown relationship types",
    snapshotFailed: "Snapshot creation failed: {{msg}}. Action aborted.",
    ameffFailed: "AMEFF import failed: {{msg}}",
    jsonFailed: "JSON import failed: {{msg}}",
    translateFailed: "Translate action hit errors; partially completed: {{msg}}",
    snapPreDedupe: "Pre-dedupe",
    snapPreAutoNest: "Pre-nesting-detect",
    snapPreLinkAttr: "Pre-attribute-link",
    snapPreTranslate: "Pre-translate-update",
    snapPreAmeff: "Pre-AMEFF-import",
    snapPreJson: "Pre-JSON-import",
  },
  moveToFolder: {
    title_one: "Move {{count}} concept to folder",
    title_other: "Move {{count}} concepts to folder",
    mixedClasses:
      "Selection has multiple type classes ({{classes}}). A folder can have only one type class. Split the selection and move per class.",
    noFoldersBefore: "No folders with type class",
    noFoldersAfter: "in Base. Create one first via Base WP → Foldertree.",
    partial: "Partial: {{moved}} moved, {{skipped}} skipped — {{reason}}",
    unknown: "unknown",
    failed: "Move failed: {{msg}}",
    confirm: "Move",
  },
  moveOutOfFolder: {
    title_one: "Move {{count}} concept out of folder",
    title_other: "Move {{count}} concepts out of folder",
    intro:
      "Concepts leave Base and get their new home in the workpackage that has write permission on this folder. The «Reference Object» stereotype is removed.",
    loadingWriteHolder: "Loading write holder…",
    noWriteHolder:
      "No workpackage has write permission on this folder. First assign write access via",
    settingsPath: "⚙ Settings → Workpackages → Folder permissions",
    targetLabel: "Target workpackage (auto)",
    failed: "Moving out of folder failed: {{msg}}",
  },
  askClaude: {
    title: "Ask Claude",
    privacyNote: "The diagram is sent to Claude (Anthropic)",
    original: "Original",
    result: "Result",
    outputLabel: "Output:",
    outputSvg: "SVG (illustration)",
    outputJson: "JSON (data)",
    outputPlantuml: "PlantUML (ArchiMate)",
    speedLabel: "Speed:",
    effort: {
      low: "Fast",
      medium: "Balanced",
      high: "Thorough",
    },
    effortHint: {
      low: "Claude thinks briefly: fastest, for simple questions",
      medium: "Usually takes one to a few minutes",
      high: "Claude thinks at length: slowest, for difficult redesigns",
    },
    suggestions: {
      managers: "Make it understandable for managers (hide the technology)",
      business: "Translate into a business context",
      customer: "Simplify for a customer presentation",
      mainProcesses: "Show only the main processes",
      layerIcons: "Add icons per ArchiMate layer",
    },
    promptPlaceholder: "What should Claude do with this diagram?  (Cmd+Enter = send)",
    submit: "Send",
    busy: "Working…",
    copy: "Copy",
    copied: "✓ copied",
    download: "Download",
    noCleanBlock: "No clean code block recognised; showing raw output",
    retryTitle:
      "Send the same prompt again — on a retry Claude sometimes does return a clean block",
    retry: "Try again",
    writing: "Claude is writing… ({{chars}} characters)",
    thinking: "Claude is thinking…",
    emptyBefore: "Enter a question on the left and press ",
    emptyMiddle: " or click ",
    errors: {
      emptyBody: "Empty response body from the worker.",
      providerAborted: "The AI provider aborted the response.",
      maxTokens:
        "Claude thought for so long that there was no room left for the answer. Try a shorter or simpler question, or a smaller diagram.",
      refusal: "Claude declined this request. Rephrase the question.",
      noAnswer: "Claude gave no answer. Please try again.",
      noAnswerWithReason: "Claude gave no answer (stop reason: {{reason}}). Please try again.",
      noAuth:
        "No sign-in with an AI provider has been configured. A super admin sets this up under Settings → Workspace → AI.",
      dailyLimit: "Daily limit reached. Try again tomorrow or ask the admin for an increase.",
      unexpectedContentType: "Unexpected content type: {{type}}",
    },
    email: {
      subject: "Ask Claude — {{prompt}}",
      subjectDefault: "Ask Claude — result",
      intro: "Result of your 'Ask Claude' request:",
      sent: "✓ Sent to {{recipient}}",
      noMailConfig: "Mail config missing — an admin must first fill in ⚙ Settings → Email.",
      buttonTitle: "Send the result as an attachment to {{email}}",
      notLoggedIn: "Not logged in",
      sending: "Sending…",
      button: "✉ Mail to me",
    },
    preview: {
      alt: "Diagram preview",
      noDiagram: "No active diagram.",
      noSummary: "(no JSON summary available)",
      rendering: "Rendering…",
      rerender: "Render again",
      renderKroki: "🖼 Render via Kroki",
      backToSourceTitle: "Back to PlantUML source",
      backToSource: "↩ Source",
      renderError: "⚠ Render error:",
      noPlantuml: "(no PlantUML available)",
    },
  },
  conceptExplorer: {
    pinnedTitle: "Pinned — panel stays open after refresh. Click to unpin.",
    pinTitle: "Pin — panel stays open after refresh.",
    unpinAria: "Unpin",
    pinAria: "Pin panel",
    onlyAllowedTypes: "Only types that fit the current diagram",
    tags: "Tags",
    noTags: "(no tags in workspace)",
    noOptions: "(no options)",
    noMatches: "No concepts found for this search term.",
    noAllowed: "No concepts of types allowed on this diagram.",
    empty: "No concepts in this workspace yet.",
    rowTitleOnDiagram: "Click = select, Cmd/Ctrl/Shift = multi-select, Delete = delete",
    rowTitleDrag:
      "Drag onto the canvas (multi-select drags all selected concepts). Delete = delete.",
    clearAllFilters: "Clear all filters",
    clear: "clear",
    usedInTitle: "Used in: {{names}}\nClick to jump to the first diagram",
  },
  history: {
    restoreConfirm:
      "Restore version {{version}} as the current one? A new version row is written; earlier versions are kept.",
    restored: "✓ Version {{version}} restored as new version {{newVersion}}.",
    titleConcept: "📜 Version history (concept)",
    titleRelation: "📜 Version history (relation)",
    close: "Close",
    noVersions: "No versions in the history yet.",
    current: "current",
    pickVersionHint:
      "Choose a version in the list to see the changes compared to the current version.",
    restoreButton: "↩ Restore this version",
    diffHeading: "What changes if you restore this version",
    noDifferences: "No differences — this version is identical to the current one.",
    colField: "Field",
    colBecomes: "Becomes (this version)",
    colWas: "Was (current)",
    showUnchanged: "Show {{n}} unchanged field(s)",
    fields: {
      parent: "Parent",
      tags: "Tags",
      deleted: "Deleted?",
      source: "Source",
      target: "Target",
    },
    valueEmpty: "(empty)",
    valueNone: "(none)",
    valueYes: "yes",
    valueNo: "no",
    reason: {
      create: "Created",
      update: "Updated",
      restore: "Restored from trash",
      restoreFromVersion: "Version {{version}} restored",
    },
    unknownError: "Unknown error",
  },
  insertRow: {
    unnamed: "(unnamed)",
    tabPlantUml: "Paste PlantUML",
    typeSearchPlaceholder: "Search type… (e.g. BusinessActor, c4, Concept)",
    noTypesFound: "No types found",
    preview: "Preview",
    previewStereotypeTitle: "Preview: «{{stereotype}}» on {{type}} ({{layer}} layer)",
    previewLayerTitle: "Preview in the layer colour of {{layer}}",
    pickTypeFirst: "Choose a type first",
    namePlaceholder: "Name (e.g. CRM system)",
    cancel: "Cancel",
    insert: "Insert",
    source: "Source",
    target: "Target",
    relationType: "Relation type",
    pickEndpointsFirst: "Choose source and target first (existing or new).",
    noAllowedRelationTypes:
      "No allowed relation types between these two types according to the matrix.",
    labelPlaceholder: "Label (optional)",
    plantUmlHint:
      "Paste a PlantUML snippet. The parser creates new concepts and relations, and updates existing ones with the same alias. Delete behaviour (rows no longer in the text) is NOT used here — this is an additive import.",
    plantUmlPlaceholder:
      '@startuml\nrectangle "Customer" as n_customer\nrectangle "CRM" as n_crm\nn_customer --> n_crm : uses\n@enduml',
    imported:
      "Imported: {{conceptsCreated}} concepts new, {{conceptsUpdated}} updated, {{relationsCreated}} relations new, {{relationsUpdated}} updated.",
    close: "Close",
    busy: "Working…",
    import: "Import",
    existing: "Existing",
    "new": "New",
    typeSearchShort: "Search type…",
    newNamePlaceholder: "Name of {{label}}…",
    notVisible: "(not visible)",
    searchPlaceholder: "Search {{label}}…",
    noResults: "No results",
  },
  diagramTabs: {
    unnamed: "(unnamed)",
    newTitle: "New diagram",
    newLabel: "New",
    menu: {
      closeDiagram: "Close diagram",
      closeOthers: "Close all diagrams except the selected one",
      closeAll: "Close all diagrams",
    },
    askClaude: "✨ Ask Claude",
    askClaudeTitle: "Send this diagram to Claude for a simplification or redesign (F9)",
    export: {
      title: "Export this diagram",
      svgFailed: "SVG export failed.",
      pngFailed: "PNG export failed.",
    },
    tabTitle: "Click = activate, double-click = rename, right-click = tab actions",
    closeTabTitle: "Close tab (diagram is kept)",
    closeTab: "Close tab",
    groups: {
      archimate: "ArchiMate",
      riskSecurity: "Risk and Security",
      sharp: "Sharp Modeling",
      c4: "C4 modelling",
      bmc: "Business Model Canvas",
      analytics: "Analytics views",
      roadmap: "Roadmap & time-travel",
    },
    create: {
      namePlaceholder: "Name",
      groupTitle: "Grouping",
      typeTitle: "Diagram type",
    },
  },
  diagramsPanel: {
    pin: {
      pinnedTitle: "Pinned — panel stays open after refresh. Click to unpin.",
      unpinnedTitle: "Pin — panel stays open after refresh.",
      unpinAria: "Unpin",
      pinAria: "Pin panel",
    },
    newPrompt: "Name of the new diagram?",
    newTitle: "New diagram of type 'Anything'",
    newButton: "+ New",
    empty: "No diagrams yet.",
    lastDiagramAlert: "The last diagram cannot be deleted.",
    lastDiagramTitle: "The last diagram cannot be deleted",
    confirmDeleteMany: "Permanently delete {{n}} diagrams?",
    confirmDeleteOnePermanent: "Permanently delete diagram '{{name}}'?",
    confirmDeleteOne: "Delete diagram '{{name}}'?",
    rowTitle: "Click = open, Cmd/Ctrl/Shift = multi-select, Delete = delete",
    tabOpen: "Tab open",
    tabClosed: "Tab closed",
    unnamed: "(unnamed)",
    unknownViewKind: "{{id}} (unknown)",
    deleteButton: "Delete diagram",
    translating: "translating…",
    properties: {
      hide: "Hide properties",
      show: "Show properties",
      label: "Properties",
      nameNl: "Name (NL)",
      nameEn: "Name (EN)",
      descriptionNl: "Description (NL)",
      descriptionEn: "Description (EN)",
      type: "Type",
      descriptionPlaceholder: "Description — appears as `title` in PlantUML",
    },
    heatMap: {
      title: "Heat-map configuration",
      target: "Target (sub)type",
      choose: "— choose —",
      vertical: "Vertical axis",
      horizontal: "Horizontal axis",
      chooseEnum: "— choose enum attribute —",
      noEnumAttrs: "This (sub)type has no enum attributes.",
    },
  },
  relationsPanel: {
    direction: {
      from: "FROM",
      fromTitle: "Show relations that start from the concept",
      to: "TO",
      toTitle: "Show relations that end at the concept",
    },
    searchPlaceholder: "Search concept by name…",
    empty: {
      noMatch: "No concepts found for this search term.",
      noOutgoing: "No concepts with outgoing relations.",
      noIncoming: "No concepts with incoming relations.",
    },
    unnamed: "(unnamed)",
    conceptRowTitle: "Click = select + expand/collapse",
    occurrences: {
      collapse: "Collapse diagram occurrences",
      expand: "Expand diagram occurrences",
      toggleTitle: "Show diagrams this relation appears on",
      none: "Not placed on any diagram",
    },
    confirmDeleteMany: "Permanently delete {{n}} relations?",
    confirmDeleteOne: "Permanently delete relation '{{type}}'{{label}} → [{{other}}]?",
    orphanTitle: "Orphaned relation — points to an unknown concept. Click + Delete to clean up.",
    rowTitle:
      "Click to select. Drag onto the canvas to place it on this diagram. Delete = remove from the workspace.",
  },
  palette: {
    title: "Type palette",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    relationHint:
      "Connect two concepts on the canvas and then pick this type from the relation menu.",
    layers: {
      strategy: "Strategy",
      common: "Common",
      business: "Business",
      application: "Application",
      technology: "Technology",
      physical: "Physical",
      implementationMigration: "Implementation & Migration",
      motivation: "Motivation",
      composite: "Composite",
    },
  },
  cascadeConfirm: {
    blockedByFacet: "'{{name}}' cannot be deleted — it is used as a facet reference by:",
    removeFacetsFirst: "Remove the facet reference(s) on those concepts first.",
    object: "Object",
    titleRelations_one: "Delete {{count}} relation from the model?",
    titleRelations_other: "Delete {{count}} relations from the model?",
    titleOne: "Delete {{kind}} '{{name}}' from the model?",
    titleMany_one: "Delete {{count}} concept from the model?",
    titleMany_other: "Delete {{count}} concepts from the model?",
    relationsGo_one: "→ {{count}} relation goes with it.",
    relationsGo_other: "→ {{count}} relations go with it.",
    standaloneGo_one: "→ {{count}} separately selected relation is deleted too.",
    standaloneGo_other: "→ {{count}} separately selected relations are deleted too.",
    onDiagrams_one: "→ Present on {{count}} diagram: {{list}}",
    onDiagrams_other: "→ Present on {{count}} diagrams: {{list}}",
    previewFailed: "(The cascade preview could not be fetched for at least one concept.)",
    toTrash: "Deleted objects and relations go to the trash; from there you can restore them.",
  },
} as const;
